// MobileUnitGob — the movement/combat behavioral core every vehicle and infantry type
// derives from: the guard/move/attack/chase state machine, tile-transition movement with
// reservation, move-wait deadlock detection, local avoid paths, move-aside chains, and
// rate-limited 16-direction firing. game/MobileUnit.cpp (whole file) + ht.h:6769-6973.
//
// Sync-RNG sites: GetIdleCountdown (`GetRandom() % 50`) and the kstHuntEnemies target
// pick (`GetRandom() % cpuntFound`) — both on ctx.rng, the lockstep stream.

import type { SimContext } from '../context.ts';
import {
  Gob,
  kfGobMobileUnit,
  kfGobFlashing,
  kfGobLayerDepthSorted,
  kfGobLayerSurfaceDecal,
} from './gob.ts';
import {
  UnitGob,
  IsGobWithinRange,
  IsTargetWithinRange,
  gmpDistFromDxy,
  ktcSightRangeMax,
} from './unitgob.ts';
import { MobileUnitConsts, kwcTileHalf, kfUntcLargeDefog } from './unitconsts.ts';
import { kgtReplicator, kgtActivator } from './gobtypes.ts';
import { kumMobileUnits } from '../player/masks.ts';
import { kfPlrComputer, kfHcapIncreasedArmor } from '../player/side.ts';
import type { Player } from '../player/player.ts';
import {
  Message,
  newMessage,
  cloneMessage,
  encodeMessageMem,
  decodeMessageMem,
  kgidNull,
  knHandled,
  knNotHandled,
  knDeleted,
  kstReservedGlobal,
  kstReservedNull,
  kmidReservedEnter,
  kmidReservedExit,
  kmidReservedUpdate,
  kmidMoveAction,
  kmidAttackAction,
  kmidGuardAction,
  kmidGuardVicinityAction,
  kmidGuardAreaAction,
  kmidHuntEnemiesAction,
  kmidMoveCommand,
  kmidAttackCommand,
  kmidAttackMoveCommand,
  kmidNearbyAllyHit,
  kmidHit,
  kmidEnemyNearby,
  kmidMoveWaitingNearby,
  kmidFireComplete,
  kmidDelete,
  kstGuard,
  kstMove,
  kstAttack,
  kstChase,
  kstHuntEnemies,
  kstDying,
  kstChangeStatePendingFireComplete,
  kstContinueActionPendingFireComplete,
  kstMinerRotateForEntry,
} from '../sm/message.ts';
import {
  kfAniLoop,
  kfAniDone,
  kfAniIgnoreFirstAdvance,
  kfAniResetWhenDone,
  kctUpdate,
} from '../anim/animation.ts';
import {
  g_mpDirToDx,
  g_mpDirToDy,
  Dir16ToDx,
  Dir16ToDy,
  Dir16IsDiagonal,
  TurnToward16,
  CalcDir16,
  kdir16S,
  kdirInvalid,
  kdirN,
  kdirNE,
  kdirE,
  kdirSE,
  kdirS,
  kdirSW,
  kdirW,
  kdirNW,
} from '../math/dir.ts';
import { TcFromWc, WcFromTc, WcTrunc } from '../math/coords.ts';
import { itofx, mulfx, subfx } from '../math/fix.ts';
import { idiv, s16 } from '../math/int32.ts';
import {
  TerrainMap,
  Path,
  TrackPoint,
  CreatePathFromPoints,
  DirectionFromLocations,
  Direction16FromLocations,
  kbfStructure,
  kbfMobileUnit,
  ganSquared,
} from '../terrain/terrainmap.ts';
import type { TPoint, WPoint } from '../types.ts';
import { Enum } from '../types.ts';
import { CreateAnimGob, kfAnmDeleteWhenDone, kfAnmSmokeFireLayer } from './animgob.ts';
import { markDeadSpan } from '../sm/stream.ts';

// game/ht.h:6769-6778
export const kmuaNone = 0;
export const kmuaGuard = 1;
export const kmuaGuardVicinity = 2;
export const kmuaGuardArea = 3;
export const kmuaMove = 4;
export const kmuaAttack = 5;
export const kmuaHuntEnemies = 6;
// Attack-move (TS-only): like kmuaMove, but the unit engages enemies it encounters en
// route regardless of its aggressiveness setting, then resumes toward the destination.
// m_mua is a serialized byte, so this added value needs no save-format change.
export const kmuaAttackMove = 7;

// Attack-move tuning (TS-only; applies ONLY to kmuaAttackMove, so pre-existing aggressive
// move/attack ACTIONS that scripted levels depend on are unchanged):
//  - acquire at SIGHT range (the remembered-enemy list is capped at ktcSightRangeMax) rather
//    than firing range, so units start closing on an en-route enemy before they can shoot it.
//  - LEASH a *diversion* (an en-route enemy, not the attack-move's primary target): once the
//    unit strays more than this many tiles from where it broke off to chase, abandon it and
//    resume the original order. The primary target of an attack-move-to-target is never leashed
//    (it IS the goal). Chebyshev tiles from the diversion anchor.
export const ktcAttackMoveAcquire = ktcSightRangeMax;
export const ktcAttackMoveLeash = 6;

// game/ht.h:6788-6807
export const kfMuntChaseEnemies = 0x0001;
export const kfMuntReturnFire = 0x0002;
export const kfMuntAttackEnemiesWhenMoving = 0x0004;
export const kfMuntAttackEnemiesWhenGuarding = 0x0008;
export const kfMuntRunAwayWhenHit = 0x0010;
export const kfMuntMoveWait = 0x0020;
export const kfMuntPathPending = 0x0040;
export const kfMuntCommandPending = 0x0080;
export const kfMuntFiring = 0x0100;
export const kfMuntMoveWaitingNearby = 0x0200;
export const kfMuntDestinationReserved = 0x0400;
export const kfMuntStuck = 0x0800;
export const kfMuntAtReplicatorInput = 0x1000;
export const kfMuntStayPut = 0x2000;
export const kfMuntAggressivenessBits =
  kfMuntChaseEnemies |
  kfMuntReturnFire |
  kfMuntAttackEnemiesWhenMoving |
  kfMuntAttackEnemiesWhenGuarding |
  kfMuntRunAwayWhenHit |
  kfMuntStayPut;

// game/ht.h:6980-6988
export const knMoveMoving = 0;
export const knMoveTargetReached = 1;
export const knMoveStuck = 2;
export const knMoveWaiting = 3;
export const kwxInvalid = -1000;

// game/res.h:657-661
export const knAggressivenessCoward = 0;
export const knAggressivenessPacifist = 1;
export const knAggressivenessSelfDefense = 2;
export const knAggressivenessDefender = 3;
export const knAggressivenessPitbull = 4;

// game/res.h:742-747
export const knGuardUnitAction = 0;
export const knGuardVicinityUnitAction = 1;
export const knGuardAreaUnitAction = 2;
export const knMoveUnitAction = 3;
export const knHuntEnemiesUnitAction = 4;
export const knMineUnitAction = 5;

// game/ht.h:303
export const knDecreasedDamagePercent = 90;

// game/MobileUnit.cpp:53 + :744-747
export const kcFireCountdown = 6;
export const ktcVicinity = 5;
export const kcupdAggressivenessBoost = 63;

// game/MobileUnit.cpp:5-21 — floor(cos(PI/4) * i + 0.5) per WCoord move distance
export const gawcDiagonalDist: number[] = [
  0, 1, 1, 2, 3, 4, 4, 5, 6, 6, 7, 8, 8, 9, 10, 11, 11, 12, 13, 13, 14, 15, 16, 16, 17, 18, 18, 19,
  20, 21, 21, 22, 23, 23, 24, 25, 25, 26, 27, 28, 28, 29, 30, 30, 31, 32, 33, 33, 34, 35, 35, 36,
  37, 37, 38, 39, 40, 40, 41, 42, 42, 43, 44, 45,
];

// game/MobileUnit.cpp:23-46 — (256 + i/2) / i, rounded step count per move distance
export const gaDiv256byNWithRounding: number[] = [
  0,
  255, // fudge to keep within 8-bit range
  128,
  85,
  64,
  51,
  43,
  37,
  32,
  28,
  26,
  23,
  21,
  20,
  18,
  17,
  16,
  15,
  14,
  13,
  13,
  12,
  12,
  11,
  11,
  10,
  10,
  9,
  9,
  9,
  9,
  8,
  8,
  8,
  8,
  7,
  7,
  7,
  7,
  7,
  6,
  6,
  6,
  6,
  6,
  6,
  6,
  5,
  5,
  5,
  5,
  5,
  5,
  5,
  5,
  5,
  5,
  4,
  4,
  4,
  4,
  4,
  4,
  4,
];

// game/MobileUnit.cpp:2384-2389 — last direction travelled -> next scan range
export const gmpDirLastDirScanStartCW: number[] = [
  kdirNE,
  kdirSE,
  kdirSE,
  kdirSW,
  kdirSW,
  kdirNW,
  kdirNW,
  kdirNE,
];
export const gmpDirLastDirScanEndCW: number[] = [
  (kdirNE - 5) & 7,
  (kdirSE - 5) & 7,
  (kdirSE - 5) & 7,
  (kdirSW - 5) & 7,
  (kdirSW - 5) & 7,
  (kdirNW - 5) & 7,
  (kdirNW - 5) & 7,
  (kdirNE - 5) & 7,
];
export const gmpDirLastDirScanStartCCW: number[] = [
  kdirNW,
  kdirNW,
  kdirNE,
  kdirNE,
  kdirSE,
  kdirSE,
  kdirSW,
  kdirSW,
];
export const gmpDirLastDirScanEndCCW: number[] = [
  (kdirNW + 5) & 7,
  (kdirNW + 5) & 7,
  (kdirNE + 5) & 7,
  (kdirNE + 5) & 7,
  (kdirSE + 5) & 7,
  (kdirSE + 5) & 7,
  (kdirSW + 5) & 7,
  (kdirSW + 5) & 7,
];

// game/MobileUnit.cpp:2644-2655 — move-aside priority: sides, backwards, then forwards
export const s_mpDirToDirsSorted: number[][] = [
  [kdirW, kdirE, kdirSW, kdirSE, kdirNW, kdirNE, kdirS, kdirN],
  [kdirNW, kdirSE, kdirW, kdirS, kdirN, kdirE, kdirSW, kdirNE],
  [kdirN, kdirS, kdirNW, kdirSW, kdirNE, kdirSE, kdirW, kdirE],
  [kdirNE, kdirSW, kdirN, kdirW, kdirE, kdirS, kdirNW, kdirSE],
  [kdirE, kdirW, kdirNE, kdirNW, kdirSE, kdirSW, kdirN, kdirS],
  [kdirSE, kdirNW, kdirE, kdirN, kdirS, kdirW, kdirNE, kdirSW],
  [kdirS, kdirN, kdirSE, kdirNE, kdirSW, kdirNW, kdirE, kdirW],
  [kdirSW, kdirNE, kdirS, kdirE, kdirW, kdirN, kdirSE, kdirNW],
];

// game/ht.h:6780-6786
export interface MoveDirections {
  pmunt: MobileUnitGob;
  idir: number;
  cdir: number;
  adir: number[];
}

// game/MobileUnit.cpp:212-233
export function AggBitsFromAgg(nAggressiveness: number): number {
  switch (nAggressiveness) {
    case knAggressivenessCoward:
      return kfMuntRunAwayWhenHit;
    case knAggressivenessSelfDefense:
      return kfMuntReturnFire | kfMuntStayPut;
    case knAggressivenessDefender:
      return kfMuntReturnFire | kfMuntAttackEnemiesWhenGuarding | kfMuntAttackEnemiesWhenMoving;
    case knAggressivenessPitbull:
      return (
        kfMuntReturnFire |
        kfMuntAttackEnemiesWhenGuarding |
        kfMuntAttackEnemiesWhenMoving |
        kfMuntChaseEnemies
      );
    // knAggressivenessPacifist: non-aggressive
  }
  return 0;
}

// game/MobileUnit.cpp:614-634
export function SendAttackCommand(ctx: SimContext, gidReceiver: number, gidTarget: number): void {
  const pgobTarget = ctx.gobm.GetGob(gidTarget);
  if (pgobTarget === null) return;
  const puntTarget = pgobTarget as UnitGob;

  const msgT = newMessage(kmidAttackCommand, gidReceiver, gidReceiver);
  const wpt: WPoint = { wx: 0, wy: 0 };
  puntTarget.GetAttackPoint(wpt);
  msgT.AttackCommand.wptTarget.wx = wpt.wx;
  msgT.AttackCommand.wptTarget.wy = wpt.wy;
  msgT.AttackCommand.gidTarget = gidTarget;
  msgT.AttackCommand.tcTargetRadius = 0;
  msgT.AttackCommand.wptTargetCenter.wx = wpt.wx;
  msgT.AttackCommand.wptTargetCenter.wy = wpt.wy;
  msgT.AttackCommand.wcMoveDistPerUpdate = 0;
  ctx.smm!.SendMsg(msgT);
}

// game/ht.h:2074-2087
export function SendMoveAction(
  ctx: SimContext,
  gidReceiver: number,
  wx: number,
  wy: number,
  tcRadius: number,
  wcMoveDistPerUpdate: number,
): void {
  const msgT = newMessage(kmidMoveAction, kgidNull, gidReceiver);
  msgT.MoveCommand.wptTarget.wx = wx;
  msgT.MoveCommand.wptTarget.wy = wy;
  msgT.MoveCommand.gidTarget = kgidNull;
  msgT.MoveCommand.wptTargetCenter.wx = wx;
  msgT.MoveCommand.wptTargetCenter.wy = wy;
  msgT.MoveCommand.tcTargetRadius = tcRadius;
  msgT.MoveCommand.wcMoveDistPerUpdate = wcMoveDistPerUpdate;
  ctx.smm!.SendMsg(msgT);
}

// game/ht.h:2089-2096
export function SendGuardAreaAction(ctx: SimContext, gidReceiver: number, nArea: number): void {
  const msgT = newMessage(kmidGuardAreaAction, kgidNull, gidReceiver);
  msgT.GuardAreaCommand.nArea = nArea;
  ctx.smm!.SendMsg(msgT);
}

// game/ht.h:2098-2104
export function SendGuardVicinityAction(ctx: SimContext, gidReceiver: number): void {
  const msgT = newMessage(kmidGuardVicinityAction, kgidNull, gidReceiver);
  ctx.smm!.SendMsg(msgT);
}

// game/ht.h:2121-2128
export function SendHuntEnemiesAction(ctx: SimContext, gidReceiver: number, um: number): void {
  const msgT = newMessage(kmidHuntEnemiesAction, kgidNull, gidReceiver);
  msgT.HuntEnemiesCommand.um = um;
  ctx.smm!.SendMsg(msgT);
}

/** The slice of ReplicatorGob MobileUnitGob::SetTarget reads (Replicator.cpp, M3). */
interface ReplicatorLike {
  GetInputTilePosition(): TPoint;
}

// game/MobileUnit.cpp:1553-1592 — static in C++; here a module function over ctx
export function AnyTransitionsIntoTile(
  ctx: SimContext,
  txDst: number,
  tyDst: number,
  pgobIgnore: Gob | null,
): MobileUnitGob | null {
  const { ctx: ctxMap, cty } = ctx.gobm.GetMapSize();

  for (let n = 0; n < g_mpDirToDx.length; n++) {
    const tx = txDst + g_mpDirToDx[n];
    const ty = tyDst + g_mpDirToDy[n];
    if (tx < 0 || tx >= ctxMap) continue;
    if (ty < 0 || ty >= cty) continue;

    for (let gid = ctx.gobm.GetFirstGid(tx, ty); gid !== kgidNull; gid = ctx.gobm.GetNextGid(gid)) {
      const pgob = ctx.gobm.GetGob(gid) as MobileUnitGob | null;
      if (pgob === null || pgob === pgobIgnore) continue;
      if (!(pgob.m_ff & kfGobMobileUnit)) continue;

      if (pgob.m_txDst === txDst && pgob.m_tyDst === tyDst) {
        if (pgob.InTransition()) return pgob;
      }
    }
  }

  return null;
}

export class MobileUnitGob extends UnitGob {
  m_dir = 0; // Direction16; the global OnEnter sets kdir16S before any use
  m_dirNext = 0;
  m_gidTarget = kgidNull;
  m_tLastFire = 0;
  m_msgPending = newMessage();
  m_msgAction = newMessage();
  m_cCountdown = 0;
  m_cMoveStepsRemaining = 0;
  m_tptChaseInitial: TPoint = { tx: 0, ty: 0 };
  m_mua = kmuaNone;
  m_muaPending = kmuaNone;
  m_wfMunt = kfMuntReturnFire | kfMuntAttackEnemiesWhenGuarding | kfMuntAttackEnemiesWhenMoving;
  m_stPending = kstReservedNull;
  m_cupdLastHitOrNearbyAllyHit = -1000000;
  m_txDst = 0;
  m_tyDst = 0;
  m_wptTarget: WPoint = { wx: kwxInvalid, wy: 0 };
  m_nSeqMoveAside = 0;
  m_ppathUnit: Path | null = null;
  m_ppathAvoid: Path | null = null;
  m_itptPath = 0;
  m_wptTargetCenter: WPoint = { wx: 0, wy: 0 };
  m_tcTargetRadius = 0;
  m_wcMoveDistPerUpdate = 0;
  // Attack-move diversion leash anchor (TS-only, RUNTIME-only — deliberately NOT serialized or
  // hashed). It's set deterministically from the lockstep command stream, so all peers agree and
  // it can't desync; the only effect of omitting it from save state is that a single-player save
  // reloaded mid-diversion-chase re-anchors at the reload point (cosmetic). Tile coords; -1 = none.
  m_txAttackMoveAnchor = -1;
  m_tyAttackMoveAnchor = -1;
  // The en-route enemy we last LEASHED off of, so the resume doesn't instantly re-grab it and
  // creep after it forever. Runtime-only like the anchor; cleared on a new diversion / guard.
  m_gidAttackMoveLeashed = kgidNull;

  // game/MobileUnit.cpp:296-385 (knVerMobileUnitGobState 11; the 8-dir migration arm
  // only fires for legacy ver-10 saves). m_msgPending/m_msgAction round-trip as the raw
  // 28-byte Message images; tLastFire as a tick delta; paths behind presence bytes.
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 11 && nVer !== 10) return false;
    this.m_dir = (pstm.ReadByte() << 24) >> 24; // (Direction) signed-char recovery
    this.m_dirNext = (pstm.ReadByte() << 24) >> 24;
    if (nVer === 10) {
      if (this.m_dir !== -1) this.m_dir = this.m_dir * 2;
      if (this.m_dirNext !== -1) this.m_dirNext = this.m_dirNext * 2;
    }
    this.m_gidTarget = pstm.ReadWord();
    this.m_tLastFire = this.ctx.sim!.GetTickCount() - pstm.ReadDword();
    this.m_msgPending = decodeMessageMem(pstm.Read(28).slice());
    this.m_msgAction = decodeMessageMem(pstm.Read(28).slice());
    this.m_cCountdown = pstm.ReadWord(); // int in C++ — ZERO-extends (MobileUnit.cpp:319)
    this.m_cMoveStepsRemaining = pstm.ReadWord(); // int in C++ — ZERO-extends (MobileUnit.cpp:320)
    this.m_tptChaseInitial.tx = (pstm.ReadWord() << 16) >> 16;
    this.m_tptChaseInitial.ty = (pstm.ReadWord() << 16) >> 16;
    this.m_txDst = (pstm.ReadWord() << 16) >> 16;
    this.m_tyDst = (pstm.ReadWord() << 16) >> 16;
    this.m_wptTarget.wx = (pstm.ReadWord() << 16) >> 16;
    this.m_wptTarget.wy = (pstm.ReadWord() << 16) >> 16;
    if (pstm.ReadByte() !== 0) {
      this.m_ppathUnit = new Path();
      if (!this.m_ppathUnit.LoadState(this.ctx.trmap!, pstm)) return false;
      this.m_itptPath = pstm.ReadWord();
    } else if (this.ctx.snapshotComplete) {
      this.m_itptPath = pstm.ReadWord(); // residual path index (see SaveState)
    }
    if (pstm.ReadByte() !== 0) {
      this.m_ppathAvoid = new Path();
      if (!this.m_ppathAvoid.LoadState(this.ctx.trmap!, pstm)) return false;
    }
    this.m_mua = pstm.ReadByte();
    this.m_muaPending = pstm.ReadByte();
    this.m_wfMunt = pstm.ReadWord();
    // byte-width truncation both ways is the C++ contract (kstReservedNull won't survive — sim-quirks B7)
    this.m_stPending = pstm.ReadByte();
    this.m_wptTargetCenter.wx = (pstm.ReadWord() << 16) >> 16;
    this.m_wptTargetCenter.wy = (pstm.ReadWord() << 16) >> 16;
    this.m_tcTargetRadius = (pstm.ReadWord() << 16) >> 16;
    this.m_wcMoveDistPerUpdate = (pstm.ReadWord() << 16) >> 16;
    this.m_cupdLastHitOrNearbyAllyHit = pstm.ReadDword() | 0;
    if (this.ctx.snapshotComplete) this.m_nSeqMoveAside = pstm.ReadWord(); // issue #96/#98 (see SaveState)
    return super.LoadState(pstm);
  }

  // game/MobileUnit.cpp:387-430

  // Dead spans (markDeadSpan, sim-quirks B15/B20): raw Message images carry stale
  // C++ union/heap bytes, and the C++-ctor-uninitialized fields (m_dirNext,
  // m_cCountdown, m_tptChaseInitial, m_wptTarget.wy, m_wptTargetCenter,
  // m_tcTargetRadius are only assigned on first move/idle/chase/attack) save heap
  // garbage for a never-touched unit where the TS has its deterministic 0. The
  // masker still compares strictly wherever the TS bytes are nonzero.

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(11);
    pstm.WriteByte(this.m_dir);
    pstm.WriteByte(this.m_dirNext);
    markDeadSpan(pstm, 1); // garbage until the first move step (MobileUnit.cpp:1892)
    pstm.WriteWord(this.m_gidTarget);
    pstm.WriteDword(this.ctx.sim!.GetTickCount() - this.m_tLastFire);
    pstm.Write(encodeMessageMem(this.m_msgPending));
    markDeadSpan(pstm, 28);
    pstm.Write(encodeMessageMem(this.m_msgAction));
    markDeadSpan(pstm, 28);
    pstm.WriteWord(this.m_cCountdown);
    markDeadSpan(pstm, 2); // garbage until the first guard-idle (MobileUnit.cpp:904)
    pstm.WriteWord(this.m_cMoveStepsRemaining);
    pstm.WriteWord(this.m_tptChaseInitial.tx);
    pstm.WriteWord(this.m_tptChaseInitial.ty);
    markDeadSpan(pstm, 4); // garbage until the first chase
    pstm.WriteWord(this.m_txDst);
    pstm.WriteWord(this.m_tyDst);
    pstm.WriteWord(this.m_wptTarget.wx);
    pstm.WriteWord(this.m_wptTarget.wy);
    markDeadSpan(pstm, 2); // the ctor inits only .wx (MobileUnit.cpp:160)
    if (this.m_ppathUnit === null) {
      pstm.WriteByte(0);
      // issue #96/#98: the faithful format only carries m_itptPath WITH a live path, but the
      // RESIDUAL index of a finished/discarded path survives in memory and is read later as a
      // repath search hint (FindNextPathTile/TrackPoint scans) — a from-scratch sim has it, so a
      // COMPLETE snapshot must carry it too or a restored unit repaths differently (this was the
      // replay viewer's checkpoint-restore divergence on ordinary games).
      if (this.ctx.snapshotComplete) pstm.WriteWord(this.m_itptPath);
    } else {
      pstm.WriteByte(1);
      this.m_ppathUnit.SaveState(pstm);
      pstm.WriteWord(this.m_itptPath);
    }
    if (this.m_ppathAvoid === null) {
      pstm.WriteByte(0);
    } else {
      pstm.WriteByte(1);
      this.m_ppathAvoid.SaveState(pstm);
    }
    pstm.WriteByte(this.m_mua);
    pstm.WriteByte(this.m_muaPending);
    pstm.WriteWord(this.m_wfMunt);
    pstm.WriteByte(this.m_stPending);
    pstm.WriteWord(this.m_wptTargetCenter.wx);
    pstm.WriteWord(this.m_wptTargetCenter.wy);
    pstm.WriteWord(this.m_tcTargetRadius);
    markDeadSpan(pstm, 6); // garbage until the first attack sets the target center
    pstm.WriteWord(this.m_wcMoveDistPerUpdate);
    pstm.WriteDword(this.m_cupdLastHitOrNearbyAllyHit);
    // issue #96/#98: the faithful C++ format omits the per-cycle move-aside sequence stamp; a
    // from-scratch sim has accumulated it (it gates the once-per-sequence step-aside,
    // MobileUnit.cpp:1969 — `m_nSeqMoveAside === ctx.nSeqMoveAside`), so a COMPLETE snapshot must
    // carry it (the global ctx.nSeqMoveAside is already in the snapshot header) for bit-exact resume.
    if (this.ctx.snapshotComplete) pstm.WriteWord(this.m_nSeqMoveAside & 0xffff);
    return super.SaveState(pstm);
  }

  get m_pmuntc(): MobileUnitConsts {
    return this.m_puntc as MobileUnitConsts;
  }

  // game/MobileUnit.cpp:155-177
  constructor(ctx: SimContext, pmuntc: MobileUnitConsts) {
    super(ctx, pmuntc);
    this.m_ff = (this.m_ff | kfGobMobileUnit) >>> 0;
  }

  private get trmap(): TerrainMap {
    return this.ctx.trmap!;
  } // gsim.GetLevel()->GetTerrainMap()

  // game/MobileUnit.cpp:184-192
  override Activate(): void {
    const tx = TcFromWc(this.m_wx);
    const ty = TcFromWc(this.m_wy);
    this.ReserveTile(tx, ty, true);
    super.Activate();
    this.ctx.gobm.MoveGobBetweenAreas(this.m_gid, 0, this.ctx.gobm.CalcAreaMask(tx, ty));
  }

  // game/MobileUnit.cpp:194-210
  override Deactivate(): void {
    if (this.m_wfMunt & kfMuntDestinationReserved) {
      this.ReserveTile(this.m_txDst, this.m_tyDst, false);
    } else {
      this.ReserveTile(TcFromWc(this.m_wx), TcFromWc(this.m_wy), false);
    }
    super.Deactivate();
    this.ctx.gobm.MoveGobBetweenAreas(
      this.m_gid,
      this.ctx.gobm.CalcAreaMask(TcFromWc(this.m_wx), TcFromWc(this.m_wy)),
      0,
    );
  }

  // game/MobileUnit.cpp:235-266 — level-file init (after the UnitGob arg parse): the
  // aggressiveness + initial Action args ("...,%d ,%a").
  override InitFromArgs(value: string): boolean {
    if (!super.InitFromArgs(value)) return false;

    const { count, values } = this.IniScanfArgs(value);
    if (count > 0) {
      this.m_wfMunt =
        (this.m_wfMunt & ~kfMuntAggressivenessBits) | AggBitsFromAgg(values[0] as number);
    }

    if (this.m_pplr.GetFlags() & kfPlrComputer) {
      if (count > 1) this.PerformAction(values[1] as string);
    } else {
      this.m_wfMunt &= ~kfMuntAttackEnemiesWhenMoving;
    }

    return true;
  }

  private IniScanfArgs(value: string): { count: number; values: (number | string)[] } {
    // The C++ scans "%*d ,%*d ,%*d ,%*d ,%*d ,%*d, %d ,%a" over the same value string
    const { count, values } = IniScanfBridge(value);
    return { count, values };
  }

  // game/MobileUnit.cpp:268-290
  override Init(wx: number, wy: number, pplr: Player, fxHealth: number, ff: number): boolean {
    if (!super.Init(wx, wy, pplr, fxHealth, ff)) return false;

    // Center the unit within its tile
    this.m_wx = s16(this.m_wx + kwcTileHalf);
    this.m_wy = s16(this.m_wy + kwcTileHalf);

    this.Activate();

    // Human-controlled units don't attack while executing a move command
    if (this.IsHumanOrGodControlled()) this.m_wfMunt &= ~kfMuntAttackEnemiesWhenMoving;

    this.NotifyEnemyNearby();

    return true;
  }

  // game/MobileUnit.cpp:404-457
  PerformAction(szAction: string): void {
    const head = IniScanf(szAction, '%d');
    if (head.count === 0) return;
    const nUnitAction = head.values[0] as number;

    switch (nUnitAction) {
      case knMoveUnitAction: {
        const r = IniScanf(szAction, '%*d ,%d');
        if (r.count === 0) return;
        const trc = this.ctx.gobm.GetAreaRect(r.values[0] as number);
        const ptx = idiv(trc.left + trc.right, 2); // TRect::GetCenter
        const pty = idiv(trc.top + trc.bottom, 2);
        SendMoveAction(
          this.ctx,
          this.m_gid,
          WcFromTc(ptx),
          WcFromTc(pty),
          1,
          this.m_pmuntc.GetMoveDistPerUpdate(),
        );
        break;
      }

      case knGuardUnitAction:
        break;

      case knGuardVicinityUnitAction:
        SendGuardVicinityAction(this.ctx, this.m_gid);
        break;

      case knGuardAreaUnitAction: {
        const r = IniScanf(szAction, '%*d ,%d');
        if (r.count === 0) return;
        SendGuardAreaAction(this.ctx, this.m_gid, r.values[0] as number);
        break;
      }

      case knHuntEnemiesUnitAction: {
        const r = IniScanf(szAction, '%*d ,%d');
        if (r.count === 0) return;
        SendHuntEnemiesAction(this.ctx, this.m_gid, r.values[0] as number);
        break;
      }
    }
  }

  // game/MobileUnit.cpp:479-483
  override IsIdle(): boolean {
    return (this.m_wfMunt & kfMuntCommandPending) === 0 && this.m_st === kstGuard;
  }

  // ht.h:6830-6852
  HasAttackTarget(): boolean {
    return this.m_gidTarget !== kgidNull;
  }
  GetMobileUnitFlags(): number {
    return this.m_wfMunt;
  }
  SetMobileUnitFlags(wf: number): void {
    this.m_wfMunt = wf & 0xffff;
  }
  ClearAction(): void {
    this.m_mua = kmuaNone;
  }
  IsHumanOrGodControlled(): boolean {
    return !(this.m_pplr.GetFlags() & kfPlrComputer) || this.ctx.fGodMode;
  }
  IsMoveWaiting(): boolean {
    return (this.m_wfMunt & kfMuntMoveWait) !== 0;
  }

  // game/MobileUnit.cpp:487-519 — rotate toward the target, rate-limit, fire
  Fire(puntTarget: UnitGob | null, wx: number, wy: number, wdx: number, wdy: number): boolean {
    const dirFire = CalcDir16(wdx, wdy);
    if (this.m_dir !== dirFire) {
      this.m_dir = TurnToward16(dirFire, this.m_dir);
      this.SetAnimationStrip(this.m_ani, this.m_pmuntc.anMovingStripIndices[this.m_dir]);
      this.m_unvl.MinSkip();
      return false;
    }

    const t = this.ctx.sim!.GetTickCount();

    const ctWait = this.m_pmuntc.ctFiringRate;
    const ctRemaining = ctWait - (t - this.m_tLastFire);
    if (ctRemaining > 0) {
      this.m_unvl.MinSkip(idiv(ctRemaining + idiv(kctUpdate, 2), kctUpdate) - 1);
      return false;
    }

    this.m_tLastFire = t;

    // Firing animation starts on frame 1, where the action is
    this.StartAnimation(
      this.m_ani,
      this.m_pmuntc.anFiringStripIndices[this.m_dir],
      1,
      kfAniIgnoreFirstAdvance | kfAniResetWhenDone,
    );
    this.m_wfMunt |= kfMuntFiring;
    this.ctx.smm!.SendDelayedMsgIds(
      kmidFireComplete,
      this.m_ani.GetRemainingStripTime(),
      this.m_gid,
      this.m_gid,
    );

    return true;
  }

  // game/MobileUnit.cpp:521-523
  Idle(): void {}

  // game/MobileUnit.cpp:525-612
  override SetTarget(
    gid: number,
    wx: number = 0,
    wy: number = 0,
    wxCenter: number = 0,
    wyCenter: number = 0,
    tcRadius: number = 0,
    wcMoveDistPerUpdate: number = 0,
  ): void {
    if (gid === kgidNull) {
      const msgT = newMessage(kmidMoveCommand, kgidNull, this.m_gid);
      msgT.MoveCommand.wptTarget.wx = wx;
      msgT.MoveCommand.wptTarget.wy = wy;
      msgT.MoveCommand.gidTarget = kgidNull;
      msgT.MoveCommand.wptTargetCenter.wx = wxCenter;
      msgT.MoveCommand.wptTargetCenter.wy = wyCenter;
      msgT.MoveCommand.tcTargetRadius = tcRadius;
      msgT.MoveCommand.wcMoveDistPerUpdate = wcMoveDistPerUpdate;
      this.ctx.cmdq.Enqueue(msgT);
      return;
    }

    const pgobTarget = this.ctx.gobm.GetGob(gid);
    if (pgobTarget === null) return;

    // A Replicator target means "move to its input". TS-only: issued as a ground
    // ATTACK-MOVE to the input point (the C++ sent a plain move, leaving the unit
    // defenseless en route), so the unit engages enemies it meets on the way and the
    // leash resumes the trip. Arrival is unchanged: CheckReplicatorPoint runs in the
    // shared movement path and parks the unit (kfMuntAtReplicatorInput) either way.
    // Unarmed units (no kfUntcNotifyEnemyNearby) never remember enemies, so for them
    // this degrades to the old plain move. Command GENERATION only — the recorded/
    // relayed command stream carries the result — so no simVersion gate.
    if (pgobTarget.GetType() === kgtReplicator) {
      pgobTarget.SetFlags((pgobTarget.GetFlags() | kfGobFlashing) >>> 0);

      const tpt = (pgobTarget as unknown as ReplicatorLike).GetInputTilePosition();
      const msgT = newMessage(kmidAttackMoveCommand, kgidNull, this.m_gid);
      msgT.AttackMoveCommand.wptTarget.wx = WcFromTc(tpt.tx) + kwcTileHalf;
      msgT.AttackMoveCommand.wptTarget.wy = WcFromTc(tpt.ty) + kwcTileHalf;
      msgT.AttackMoveCommand.gidTarget = kgidNull;
      msgT.AttackMoveCommand.wptTargetCenter.wx = msgT.AttackMoveCommand.wptTarget.wx;
      msgT.AttackMoveCommand.wptTargetCenter.wy = msgT.AttackMoveCommand.wptTarget.wy;
      msgT.AttackMoveCommand.tcTargetRadius = 0;
      msgT.AttackMoveCommand.wcMoveDistPerUpdate = wcMoveDistPerUpdate;
      this.ctx.cmdq.Enqueue(msgT);
      return;
    }

    if (wx === 0 && wy === 0) {
      const puntTarget = pgobTarget as UnitGob;
      const wpt: WPoint = { wx: 0, wy: 0 };
      puntTarget.GetAttackPoint(wpt);
      wx = wpt.wx;
      wy = wpt.wy;
    }

    // Friend or foe? No special action for friends
    if (this.IsAlly(pgobTarget.GetSide())) return;

    if (this.m_pplr === this.ctx.plrLocal) pgobTarget.Flash();

    const msgT = newMessage(kmidAttackCommand, this.m_gid, this.m_gid);
    msgT.AttackCommand.wptTarget.wx = wx;
    msgT.AttackCommand.wptTarget.wy = wy;
    msgT.AttackCommand.gidTarget = gid;
    msgT.AttackCommand.wptTargetCenter.wx = wxCenter;
    msgT.AttackCommand.wptTargetCenter.wy = wyCenter;
    msgT.AttackCommand.tcTargetRadius = tcRadius;
    msgT.AttackCommand.wcMoveDistPerUpdate = wcMoveDistPerUpdate;
    this.ctx.cmdq.Enqueue(msgT);
  }

  /** Attack-move (TS-only): engage enemies en route, then either resume to a ground point
   *  (gid === kgidNull) or finish on a target gob (gid set). Enqueues kmidAttackMoveCommand;
   *  the formation geometry mirrors SetTarget's move/attack arms. */
  SetAttackMoveTarget(
    gid: number,
    wx: number,
    wy: number,
    wxCenter: number,
    wyCenter: number,
    tcRadius: number,
    wcMoveDistPerUpdate: number,
  ): void {
    // A Replicator is a dock, never an attack target — route it through SetTarget,
    // which issues the attack-move to the replicator's INPUT POINT.
    if (gid !== kgidNull && this.ctx.gobm.GetGob(gid)?.GetType() === kgtReplicator) {
      this.SetTarget(gid, wx, wy, wxCenter, wyCenter, tcRadius, wcMoveDistPerUpdate);
      return;
    }
    // A targeted attack-move flashes the target RED (like a normal attack flashes it white),
    // rather than dropping a ground reticle on it. Local player only — purely presentation.
    if (gid !== kgidNull && this.m_pplr === this.ctx.plrLocal) {
      this.ctx.gobm.GetGob(gid)?.FlashAttackMove();
    }
    const msgT = newMessage(kmidAttackMoveCommand, kgidNull, this.m_gid);
    msgT.AttackMoveCommand.wptTarget.wx = wx;
    msgT.AttackMoveCommand.wptTarget.wy = wy;
    msgT.AttackMoveCommand.gidTarget = gid;
    msgT.AttackMoveCommand.wptTargetCenter.wx = wxCenter;
    msgT.AttackMoveCommand.wptTargetCenter.wy = wyCenter;
    msgT.AttackMoveCommand.tcTargetRadius = tcRadius;
    msgT.AttackMoveCommand.wcMoveDistPerUpdate = wcMoveDistPerUpdate;
    this.ctx.cmdq.Enqueue(msgT);
  }

  // game/MobileUnit.cpp:636-647 — the attack point must be terrain accessible
  override GetAttackPoint(pwpt: WPoint): void {
    if (this.m_wfMunt & kfMuntDestinationReserved) {
      pwpt.wx = WcFromTc(this.m_txDst);
      pwpt.wy = WcFromTc(this.m_tyDst);
    } else {
      pwpt.wx = this.m_wx;
      pwpt.wy = this.m_wy;
    }
  }

  // game/MobileUnit.cpp:663-674
  private PendOrProcessCommand(pmsg: Message, stNew: number): boolean {
    if (!this.IsReadyForCommand()) {
      this.m_msgPending = cloneMessage(pmsg);
      this.m_wfMunt |= kfMuntCommandPending;
      return false;
    }
    this.m_gidTarget = kgidNull;
    this.SetState(stNew);
    return true;
  }

  // game/MobileUnit.cpp:676-690
  private PendOrProcessAction(pmsg: Message, stNew: number, mua: number): boolean {
    if (!this.IsReadyForCommand()) {
      this.m_muaPending = mua;
      this.m_msgPending = cloneMessage(pmsg);
      this.m_wfMunt |= kfMuntCommandPending;
      return false;
    }
    this.m_mua = mua;
    this.m_gidTarget = kgidNull;
    this.m_msgAction = cloneMessage(pmsg);
    this.SetState(stNew);
    return true;
  }

  // game/MobileUnit.cpp:692-698
  private IsTargetInRange(): boolean {
    const pgobTarget = this.ctx.gobm.GetGob(this.m_gidTarget);
    if (pgobTarget === null) return false;
    return IsGobWithinRange(this, pgobTarget, this.m_pmuntc.tcFiringRange);
  }

  // game/MobileUnit.cpp:700-711
  IsStandingOnActivator(): boolean {
    for (
      let gid = this.ctx.gobm.GetFirstGid(TcFromWc(this.m_wx), TcFromWc(this.m_wy));
      gid !== kgidNull;
      gid = this.ctx.gobm.GetNextGid(gid)
    ) {
      const pgob = this.ctx.gobm.GetGob(gid, false);
      if (pgob === null) continue;
      if (pgob.GetType() === kgtActivator) return true;
    }
    return false;
  }

  // game/MobileUnit.cpp:713-721
  private SetStatePendingFireComplete(st: number): void {
    if (this.m_wfMunt & kfMuntFiring) {
      this.m_stPending = st;
      this.SetState(kstChangeStatePendingFireComplete);
    } else {
      this.SetState(st);
    }
  }

  // game/MobileUnit.cpp:723-730
  private ContinueActionPendingFireComplete(): void {
    if (this.m_wfMunt & kfMuntFiring) {
      this.SetState(kstContinueActionPendingFireComplete);
    } else {
      this.ctx.smm!.SendMsg(this.m_msgAction);
    }
  }

  // game/MobileUnit.cpp:732-737
  private IsAttackPointWithinFiringRangeOfTarget(puntTarget: UnitGob): boolean {
    const wptAttack: WPoint = { wx: 0, wy: 0 };
    puntTarget.GetAttackPoint(wptAttack);
    return IsTargetWithinRange(wptAttack, puntTarget, this.m_pmuntc.tcFiringRange);
  }

  // ── attack-move leash (TS-only) ──
  /** Anchor the diversion leash at the unit's current tile (where it broke off to chase). */
  private SetAttackMoveLeashAnchor(): void {
    this.m_txAttackMoveAnchor = TcFromWc(this.m_wx);
    this.m_tyAttackMoveAnchor = TcFromWc(this.m_wy);
    this.m_gidAttackMoveLeashed = kgidNull; // a fresh diversion clears the prior leash memory
  }
  /** Is the current chase target a leashed DIVERSION (an en-route enemy), not the attack-move's
   *  primary target? A ground attack-move has no primary target, so every chase is a diversion. */
  private IsAttackMoveDiversionTarget(): boolean {
    const gidPrimary = this.m_msgAction.AttackMoveCommand.gidTarget;
    return gidPrimary === kgidNull || this.m_gidTarget !== gidPrimary;
  }
  /** Has the unit wandered more than ktcAttackMoveLeash tiles from the diversion anchor? */
  private AttackMoveLeashExceeded(): boolean {
    const dtx = Math.abs(TcFromWc(this.m_wx) - this.m_txAttackMoveAnchor);
    const dty = Math.abs(TcFromWc(this.m_wy) - this.m_tyAttackMoveAnchor);
    return dtx > ktcAttackMoveLeash || dty > ktcAttackMoveLeash;
  }

  // game/MobileUnit.cpp:739-742 — SYNC RNG
  GetIdleCountdown(): number {
    return (this.ctx.rng.GetRandom() % 50) + 50; // somewhere between 4 & 8 seconds
  }

  // ===== the state machine — game/MobileUnit.cpp:749-1438 =====

  /** lbMoveCommand (MobileUnit.cpp:782-792) — move params stashed for the move state. */
  private stashMoveParams(pmsg: Message): void {
    this.m_wptTarget.wx = WcTrunc(pmsg.MoveCommand.wptTarget.wx) + kwcTileHalf;
    this.m_wptTarget.wy = WcTrunc(pmsg.MoveCommand.wptTarget.wy) + kwcTileHalf;
    this.m_wptTargetCenter.wx = WcTrunc(pmsg.MoveCommand.wptTargetCenter.wx) + kwcTileHalf;
    this.m_wptTargetCenter.wy = WcTrunc(pmsg.MoveCommand.wptTargetCenter.wy) + kwcTileHalf;
    this.m_tcTargetRadius = pmsg.MoveCommand.tcTargetRadius;
    this.m_wcMoveDistPerUpdate = pmsg.MoveCommand.wcMoveDistPerUpdate;
  }

  /** lbAttackCommand (MobileUnit.cpp:796-806) — attack params stashed for attack state. */
  private stashAttackParams(pmsg: Message): void {
    this.m_gidTarget = pmsg.AttackCommand.gidTarget;
    this.m_wptTarget.wx = WcTrunc(pmsg.AttackCommand.wptTarget.wx) + kwcTileHalf;
    this.m_wptTarget.wy = WcTrunc(pmsg.AttackCommand.wptTarget.wy) + kwcTileHalf;
    this.m_wptTargetCenter.wx = WcTrunc(pmsg.AttackCommand.wptTargetCenter.wx) + kwcTileHalf;
    this.m_wptTargetCenter.wy = WcTrunc(pmsg.AttackCommand.wptTargetCenter.wy) + kwcTileHalf;
    this.m_tcTargetRadius = pmsg.AttackCommand.tcTargetRadius;
    this.m_wcMoveDistPerUpdate = pmsg.AttackCommand.wcMoveDistPerUpdate;
  }

  /** lbHitNoDamage (MobileUnit.cpp:843-866) — think about fighting back. */
  private hitFightBack(pmsg: Message): void {
    if (this.m_wfMunt & kfMuntRunAwayWhenHit) {
      // UNDONE in the C++: knAggressivenessCoward
    } else if (this.m_wfMunt & kfMuntReturnFire && pmsg.Hit.gidAssailant !== kgidNull) {
      // ...and we're not already attacking an in-range target...
      if (this.m_gidTarget === kgidNull || !this.IsTargetInRange()) {
        // ...and we're not on a human-directed mission: fight back!
        if (this.m_pplr.GetFlags() & kfPlrComputer || this.m_st === kstGuard) {
          SendAttackCommand(this.ctx, this.m_gid, pmsg.Hit.gidAssailant);
        }
      }
    }
  }

  override ProcessStateMachineMessage(st: number, pmsg: Message): number {
    if (st === kstReservedGlobal) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          this.m_dir = kdir16S;
          this.SetState(kstGuard);
          return knHandled;

        // Actions are sent by Trigger/UnitActions or the Overmind

        case kmidMoveAction:
          if (this.PendOrProcessAction(pmsg, kstMove, kmuaMove)) this.stashMoveParams(pmsg);
          return knHandled;

        case kmidAttackAction:
          if (this.PendOrProcessAction(pmsg, kstAttack, kmuaAttack)) this.stashAttackParams(pmsg);
          return knHandled;

        case kmidGuardAction:
          this.PendOrProcessAction(pmsg, kstGuard, kmuaGuard);
          return knHandled;

        case kmidGuardVicinityAction:
          this.PendOrProcessAction(pmsg, kstGuard, kmuaGuardVicinity);
          return knHandled;

        case kmidGuardAreaAction:
          this.PendOrProcessAction(pmsg, kstGuard, kmuaGuardArea);
          return knHandled;

        case kmidHuntEnemiesAction:
          this.PendOrProcessAction(pmsg, kstHuntEnemies, kmuaHuntEnemies);
          return knHandled;

        // Commands are the result of player interactions with the SimUI

        case kmidMoveCommand:
          if (this.PendOrProcessCommand(pmsg, kstMove)) this.stashMoveParams(pmsg);
          return knHandled;

        case kmidAttackCommand:
          if (this.PendOrProcessCommand(pmsg, kstAttack)) this.stashAttackParams(pmsg);
          return knHandled;

        // Attack-move: an ACTION (m_mua = kmuaAttackMove) so the existing resume-after-kill
        // path (kstAttack -> ContinueActionPendingFireComplete) carries the unit onward.
        //  - no gidTarget  -> attack-move to a GROUND point: enter kstMove, engage en route.
        //  - with gidTarget -> attack-move to a TARGET gob: enter kstAttack, fight through
        //    en-route enemies, finish on the target (the target IS the destination).
        case kmidAttackMoveCommand:
          if (pmsg.AttackMoveCommand.gidTarget !== kgidNull) {
            if (this.PendOrProcessAction(pmsg, kstAttack, kmuaAttackMove))
              this.stashAttackParams(pmsg);
          } else {
            if (this.PendOrProcessAction(pmsg, kstMove, kmuaAttackMove)) this.stashMoveParams(pmsg);
          }
          return knHandled;

        case kmidNearbyAllyHit:
          this.m_cupdLastHitOrNearbyAllyHit = this.ctx.sim!.GetUpdateCount();
          // Respond pretty much as if self was the one hit (goto lbHitNoDamage)
          this.hitFightBack(pmsg);
          return knHandled;

        case kmidHit: {
          this.m_cupdLastHitOrNearbyAllyHit = this.ctx.sim!.GetUpdateCount();

          let fxDamage = itofx(pmsg.Hit.nDamage);
          if (this.m_pplr.GetHandicap() & kfHcapIncreasedArmor) {
            fxDamage = mulfx(fxDamage, idiv(itofx(knDecreasedDamagePercent), 100));
          }
          this.SetHealth(subfx(this.m_fxHealth, fxDamage));

          if (this.m_fxHealth <= 0) {
            const pplr = this.ctx.plrm.GetPlayer(pmsg.Hit.sideAssailant)!;
            pplr.IncEnemyMobileUnitsKilled();
            this.m_pplr.IncMobileUnitsLost();
            this.SetState(kstDying);
          } else {
            this.ShowDamageIndicator();
            this.hitFightBack(pmsg);
          }

          this.NotifyNearbyAlliesOfHit(pmsg.Hit.gidAssailant);
          return knHandled;
        }

        case kmidEnemyNearby:
          this.RememberEnemyNearby(pmsg.EnemyNearby.gidEnemy);
          this.m_unvl.MinSkip(); // wake up and check out the enemy
          return knHandled;

        case kmidMoveWaitingNearby:
          this.m_unvl.MinSkip();
          return knHandled;

        case kmidFireComplete:
          this.m_wfMunt &= ~kfMuntFiring;
          this.m_unvl.MinSkip();
          return knHandled;

        case kmidDelete:
          return knHandled; // C++ asserts (shouldn't arrive outside kstDying)
      }
      return knNotHandled;
    }

    //-----------------------------------------------------------------------
    if (st === kstGuard) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          this.StartAnimation(
            this.m_ani,
            this.m_pmuntc.anIdleStripIndices[this.m_dir],
            0,
            kfAniDone,
          );
          this.m_wptTarget.wx = kwxInvalid;
          this.m_gidTarget = kgidNull;
          // attack-move is over once we guard — clear its runtime leash state (TS-only).
          this.m_txAttackMoveAnchor = -1;
          this.m_gidAttackMoveLeashed = kgidNull;
          this.m_cCountdown = this.GetIdleCountdown();
          if (this.m_wfMunt & kfMuntMoveWaitingNearby) {
            this.NotifyMoveWaitingNearby(this.m_wx, this.m_wy);
          }
          return knHandled;

        case kmidReservedUpdate: {
          // A command might have been pended while waiting for the firing anim
          if (this.m_wfMunt & kfMuntCommandPending && this.IsReadyForCommand()) {
            this.m_wfMunt &= ~kfMuntCommandPending;
            this.ctx.smm!.SendMsg(this.m_msgPending);
            return knHandled;
          }

          // If an enemy is in range and we're not already firing at something
          if (
            (this.m_wfMunt & (kfMuntAttackEnemiesWhenGuarding | kfMuntFiring)) ===
            kfMuntAttackEnemiesWhenGuarding
          ) {
            const tcSightRange =
              this.m_mua === kmuaGuardVicinity ? ktcVicinity : this.m_pmuntc.tcFiringRange;
            const puntTarget = this.FindEnemyNearby(tcSightRange);
            if (puntTarget !== null) {
              if (this.m_pplr.GetFlags() & kfPlrComputer) {
                puntTarget.GetAttackPoint(this.m_wptTarget);
                this.m_gidTarget = puntTarget.GetId();
                this.m_wptTargetCenter = { ...this.m_wptTarget };
                this.m_tcTargetRadius = 0;
                this.m_wcMoveDistPerUpdate = this.m_pmuntc.GetMoveDistPerUpdate();
                this.SetStatePendingFireComplete(kstAttack);

                // Pretend we're hit so nearby units come help
                this.NotifyNearbyAlliesOfHit(this.m_gidTarget);
                return knHandled;
              }

              puntTarget.GetCenter(this.m_wptTarget);
              this.Fire(
                puntTarget,
                this.m_wptTarget.wx,
                this.m_wptTarget.wy,
                this.m_wptTarget.wx - this.m_wx,
                this.m_wptTarget.wy - this.m_wy,
              );
              this.m_cCountdown = this.GetIdleCountdown();

              this.NotifyNearbyAlliesOfHit(puntTarget.GetId());
            } else if (this.m_mua === kmuaGuardArea) {
              const puntTarget2 = this.FindValidTargetInArea(
                this.m_msgAction.GuardAreaCommand.nArea,
              );
              if (puntTarget2 !== null) {
                puntTarget2.GetAttackPoint(this.m_wptTarget);
                this.m_gidTarget = puntTarget2.GetId();
                this.m_wptTargetCenter = { ...this.m_wptTarget };
                this.m_tcTargetRadius = 0;
                this.m_wcMoveDistPerUpdate = this.m_pmuntc.GetMoveDistPerUpdate();
                this.SetStatePendingFireComplete(kstAttack);
                return knHandled;
              }
            } else {
              this.m_wptTarget.wx = kwxInvalid;
              this.m_gidTarget = kgidNull;
            }
          }

          this.AdvanceAnimation(this.m_ani); // idle or firing

          this.m_cCountdown -= this.m_unvl.GetUpdateCount();
          if (this.m_cCountdown < 0) {
            this.Idle();
            this.m_cCountdown = this.GetIdleCountdown();
          }
          this.m_unvl.MinSkip(this.m_cCountdown);

          this.DefUpdate(); // flashing
          return knHandled;
        }
      }
      return knNotHandled;
    }

    //-----------------------------------------------------------------------
    if (st === kstMove) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          this.MoveEnter();
          return knHandled;

        case kmidReservedExit:
          this.MoveExit();
          return knHandled;

        case kmidReservedUpdate: {
          if (!(this.m_wfMunt & kfMuntCommandPending) && !this.InTransition()) {
            // A sufficiently aggressive unit attacks enemies it sees while moving. An explicit
            // attack-move order (kmuaAttackMove) forces this regardless of aggressiveness, and
            // acquires at the wider SIGHT range so it closes in before it can fire.
            if (this.m_wfMunt & kfMuntAttackEnemiesWhenMoving || this.m_mua === kmuaAttackMove) {
              const tcAcquire =
                this.m_mua === kmuaAttackMove ? ktcAttackMoveAcquire : this.m_pmuntc.tcFiringRange;
              let puntTarget = this.FindEnemyNearby(tcAcquire);
              if (
                this.m_mua === kmuaAttackMove &&
                puntTarget !== null &&
                puntTarget.GetId() === this.m_gidAttackMoveLeashed
              ) {
                puntTarget = null; // don't re-grab the enemy we just leashed off of
              }
              if (puntTarget !== null) {
                this.m_gidTarget = puntTarget.GetId();
                puntTarget.GetAttackPoint(this.m_wptTarget);
                this.m_wptTargetCenter = { ...this.m_wptTarget };
                this.m_tcTargetRadius = 0;
                this.m_wcMoveDistPerUpdate = this.m_pmuntc.GetMoveDistPerUpdate();
                // ground attack-move: every engagement is a diversion — anchor the leash here.
                if (this.m_mua === kmuaAttackMove) this.SetAttackMoveLeashAnchor();
                this.SetState(kstAttack);
                return knHandled;
              }
            }
          }

          switch (this.MoveUpdate()) {
            case knMoveTargetReached:
              // move (or attack-move) action complete
              if (this.m_mua === kmuaMove || this.m_mua === kmuaAttackMove) this.m_mua = kmuaNone;
              this.SetState(kstGuard);
              break;

            case knMoveStuck:
              // Give up and return to guard state (see the C++ comment)
              if (this.m_mua === kmuaMove || this.m_mua === kmuaAttackMove) this.m_mua = kmuaNone;
              this.SetState(kstGuard);
              break;
          }
          return knHandled;
        }
      }
      return knNotHandled;
    }

    //-----------------------------------------------------------------------
    if (st === kstAttack) {
      switch (pmsg.mid) {
        case kmidReservedExit:
          // Notify any gobs waiting on this gob to leave attack state
          if (this.m_wfMunt & kfMuntMoveWaitingNearby) {
            this.NotifyMoveWaitingNearby(this.m_wx, this.m_wy);
          }
          return knHandled;

        case kmidReservedUpdate: {
          // A command might have been pended while waiting for the firing anim
          if (this.m_wfMunt & kfMuntCommandPending && this.IsReadyForCommand()) {
            this.m_wfMunt &= ~kfMuntCommandPending;
            this.ctx.smm!.SendMsg(this.m_msgPending);
            return knHandled;
          }

          // If the enemy is dead/gone/taken over, go to guard mode
          const puntTarget = this.ctx.gobm.GetGob(this.m_gidTarget) as UnitGob | null;
          if (puntTarget === null || !this.IsValidTarget(puntTarget)) {
            // A completed Attack action is done
            if (
              this.m_mua === kmuaAttack &&
              this.m_gidTarget === this.m_msgAction.AttackCommand.gidTarget
            ) {
              this.m_mua = kmuaNone;
            } else if (
              this.m_mua === kmuaAttackMove &&
              this.m_msgAction.AttackMoveCommand.gidTarget !== kgidNull &&
              this.m_gidTarget === this.m_msgAction.AttackMoveCommand.gidTarget
            ) {
              // attack-move-to-TARGET: the target was the destination — the order is done
              // (an en-route enemy has a different gid, so it falls through to ContinueAction).
              this.m_mua = kmuaNone;
            } else if (this.m_mua !== kmuaNone) {
              // Some other action was interrupted to attack: return to it
              this.m_gidTarget = kgidNull;
              this.ContinueActionPendingFireComplete();
              return knHandled;
            }

            this.m_gidTarget = kgidNull;
            this.SetStatePendingFireComplete(kstGuard);
          } else {
            // If the enemy isn't in range, think about chasing it
            if (!IsGobWithinRange(this, puntTarget, this.m_pmuntc.tcFiringRange)) {
              if (
                !(this.m_wfMunt & kfMuntStayPut) &&
                (this.m_mua !== kmuaNone || !this.IsStandingOnActivator()) &&
                ((this.m_wfMunt & kfMuntChaseEnemies) !== 0 ||
                  this.ctx.sim!.GetUpdateCount() - this.m_cupdLastHitOrNearbyAllyHit <=
                    kcupdAggressivenessBoost ||
                  (this.m_mua === kmuaAttack &&
                    this.m_gidTarget === this.m_msgAction.AttackCommand.gidTarget) ||
                  (this.m_mua === kmuaGuardArea &&
                    this.ctx.gobm.IsGobWithinArea(
                      puntTarget,
                      this.m_msgAction.GuardAreaCommand.nArea,
                    )) ||
                  this.m_mua === kmuaGuardVicinity ||
                  this.m_mua === kmuaHuntEnemies ||
                  this.IsHumanOrGodControlled()) &&
                this.IsAttackPointWithinFiringRangeOfTarget(puntTarget)
              ) {
                this.SetStatePendingFireComplete(kstChase);
              } else if (this.m_mua !== kmuaNone) {
                this.ContinueActionPendingFireComplete();
                return knHandled;
              } else {
                this.SetStatePendingFireComplete(kstGuard);
              }
            } else {
              // Otherwise, fire at the enemy
              if (!(this.m_wfMunt & kfMuntFiring)) {
                puntTarget.GetAttackPoint(this.m_wptTarget);
                const wptFire: WPoint = { wx: 0, wy: 0 };
                puntTarget.GetCenter(wptFire);
                this.Fire(
                  puntTarget,
                  wptFire.wx,
                  wptFire.wy,
                  wptFire.wx - this.m_wx,
                  wptFire.wy - this.m_wy,
                );
              }
            }
          }

          this.AdvanceAnimation(this.m_ani);
          this.DefUpdate(); // flashing
          return knHandled;
        }
      }
      return knNotHandled;
    }

    //-----------------------------------------------------------------------
    if (st === kstChase) {
      switch (pmsg.mid) {
        case kmidReservedEnter: {
          this.MoveEnter();

          // Remember where the target was found; if it moves we repath
          const pgobTarget = this.ctx.gobm.GetGob(this.m_gidTarget);
          if (pgobTarget !== null) {
            const puntTarget = pgobTarget as UnitGob;
            const wptTarget: WPoint = { wx: 0, wy: 0 };
            puntTarget.GetAttackPoint(wptTarget);
            this.m_tptChaseInitial.tx = TcFromWc(wptTarget.wx);
            this.m_tptChaseInitial.ty = TcFromWc(wptTarget.wy);
          }
          return knHandled;
        }

        case kmidReservedExit:
          this.MoveExit();
          return knHandled;

        case kmidReservedUpdate: {
          if (this.IsReadyForCommand()) {
            if (this.m_wfMunt & kfMuntCommandPending) {
              this.m_wfMunt &= ~kfMuntCommandPending;
              this.ctx.smm!.SendMsg(this.m_msgPending);
              return knHandled;
            }

            // Attack-move LEASH: a diverted en-route enemy that has lured us more than
            // ktcAttackMoveLeash tiles from where we broke off is abandoned — resume the
            // original order. The primary target of an attack-move-to-target is the goal and
            // is never leashed. (Gated on kmuaAttackMove, so other actions are unaffected.)
            if (
              this.m_mua === kmuaAttackMove &&
              this.m_txAttackMoveAnchor >= 0 &&
              this.IsAttackMoveDiversionTarget() &&
              this.AttackMoveLeashExceeded()
            ) {
              this.m_gidAttackMoveLeashed = this.m_gidTarget; // don't immediately re-grab it
              this.m_gidTarget = kgidNull;
              this.m_txAttackMoveAnchor = -1;
              this.ctx.smm!.SendMsg(this.m_msgAction); // resume the attack-move order
              return knHandled;
            }

            // An AttackAction-executing aggressive unit attacks what it sees while closing on
            // its target. Attack-move-to-target (kmuaAttackMove) forces this regardless of
            // aggressiveness so the unit fights through en-route enemies before the target,
            // acquiring at the wider SIGHT range.
            if (this.m_mua === kmuaAttack || this.m_mua === kmuaAttackMove) {
              if (this.m_wfMunt & kfMuntAttackEnemiesWhenMoving || this.m_mua === kmuaAttackMove) {
                const tcAcquire =
                  this.m_mua === kmuaAttackMove
                    ? ktcAttackMoveAcquire
                    : this.m_pmuntc.tcFiringRange;
                let puntTarget = this.FindEnemyNearby(tcAcquire);
                if (
                  this.m_mua === kmuaAttackMove &&
                  puntTarget !== null &&
                  puntTarget.GetId() === this.m_gidAttackMoveLeashed
                ) {
                  puntTarget = null; // don't re-grab the enemy we just leashed off of
                }
                if (puntTarget !== null && puntTarget.GetId() !== this.m_gidTarget) {
                  this.m_gidTarget = puntTarget.GetId();
                  puntTarget.GetAttackPoint(this.m_wptTarget);
                  if (this.m_mua === kmuaAttackMove) this.SetAttackMoveLeashAnchor();
                  this.SetState(kstAttack);
                  return knHandled;
                }
              }
            }

            // Verify the target still exists
            const pgobTarget = this.ctx.gobm.GetGob(this.m_gidTarget);
            if (pgobTarget === null || !this.IsValidTarget(pgobTarget)) {
              if (this.m_mua !== kmuaNone) {
                this.m_gidTarget = kgidNull;
                this.ctx.smm!.SendMsg(this.m_msgAction);
                return knHandled;
              }
              this.m_gidTarget = kgidNull;
              this.SetState(kstGuard);
              return knHandled;
            }

            // If the enemy is in range, drop back into attack mode
            if (IsGobWithinRange(this, pgobTarget, this.m_pmuntc.tcFiringRange)) {
              this.SetState(kstAttack);
              return knHandled;
            } else if (!(this.m_wfMunt & kfMuntChaseEnemies) && !this.IsHumanOrGodControlled()) {
              if (this.m_mua === kmuaGuardArea) {
                // Guarding an area the enemy has left: let it go
                if (
                  !this.ctx.gobm.IsGobWithinArea(
                    pgobTarget,
                    this.m_msgAction.GuardAreaCommand.nArea,
                  )
                ) {
                  this.SetState(kstGuard);
                  return knHandled;
                }
              } else if (this.m_mua === kmuaGuardVicinity) {
                if (
                  !IsGobWithinRange(this, pgobTarget, ktcVicinity) &&
                  this.ctx.sim!.GetUpdateCount() - this.m_cupdLastHitOrNearbyAllyHit >
                    kcupdAggressivenessBoost
                ) {
                  this.SetState(kstGuard);
                  return knHandled;
                }
              } else if (this.m_mua === kmuaHuntEnemies || this.m_mua === kmuaAttack) {
                // Inherently chase modes: stick with it
              } else if (
                this.ctx.sim!.GetUpdateCount() - this.m_cupdLastHitOrNearbyAllyHit >
                kcupdAggressivenessBoost
              ) {
                // No aggressiveness boost anymore: drop back to the action or Guard
                if (this.m_mua !== kmuaNone) this.ctx.smm!.SendMsg(this.m_msgAction);
                else this.SetState(kstGuard);
                return knHandled;
              }
            }

            // If the target moved, force a new path
            const puntTarget = pgobTarget as UnitGob;
            const wptTarget: WPoint = { wx: 0, wy: 0 };
            puntTarget.GetAttackPoint(wptTarget);
            const tx = TcFromWc(wptTarget.wx);
            const ty = TcFromWc(wptTarget.wy);
            if (tx !== this.m_tptChaseInitial.tx || ty !== this.m_tptChaseInitial.ty) {
              this.m_tptChaseInitial.tx = tx;
              this.m_tptChaseInitial.ty = ty;
              this.m_wptTarget = { ...wptTarget };
              this.MoveExit();
            }
          }

          switch (this.MoveUpdate()) {
            case knMoveTargetReached:
              this.SetState(kstAttack);
              return knHandled;

            case knMoveStuck: {
              // For the next few updates, use the target gob's center
              const puntTarget = this.ctx.gobm.GetGob(this.m_gidTarget) as UnitGob | null;
              if (puntTarget !== null) {
                const wptTarget: WPoint = { wx: 0, wy: 0 };
                puntTarget.GetAttackPoint(wptTarget);
                if (
                  WcTrunc(wptTarget.wx) !== WcTrunc(this.m_wptTarget.wx) ||
                  WcTrunc(wptTarget.wy) !== WcTrunc(this.m_wptTarget.wy)
                ) {
                  this.m_wptTarget = { ...wptTarget };
                  this.m_unvl.MinSkip(12);
                  break;
                }
              }

              this.m_gidTarget = kgidNull;
              this.SetState(kstGuard);
              return knHandled;
            }
          }
          return knHandled;
        }
      }
      return knNotHandled;
    }

    //-----------------------------------------------------------------------
    // Hunting units sit idle if they can't find a target; they only return fire
    if (st === kstHuntEnemies) {
      switch (pmsg.mid) {
        case kmidReservedEnter:
          this.StartAnimation(
            this.m_ani,
            this.m_pmuntc.anIdleStripIndices[this.m_dir],
            0,
            kfAniDone,
          );
          this.m_wptTarget.wx = kwxInvalid;
          this.m_gidTarget = kgidNull;
          this.m_cCountdown = this.GetIdleCountdown();
          return knHandled;

        case kmidReservedUpdate: {
          const pgobTarget = this.ctx.gobm.GetGob(this.m_gidTarget);
          if (pgobTarget === null || !this.IsValidTarget(pgobTarget)) {
            const apuntFound: UnitGob[] = [];

            // Find an enemy to attack
            for (
              let pgob = this.ctx.gobm.GetFirstGob();
              pgob !== null;
              pgob = this.ctx.gobm.GetNextGob(pgob)
            ) {
              if (!this.IsValidTarget(pgob)) continue;
              const punt = pgob as UnitGob; // only Units pass IsValidTarget

              // Is it one of the unit types we're supposed to hunt?
              if (!((punt.GetConsts().um & this.m_msgAction.HuntEnemiesCommand.um) >>> 0)) continue;

              apuntFound.push(punt);
            }

            if (apuntFound.length > 0) {
              const punt = apuntFound[this.ctx.rng.GetRandom() % apuntFound.length]; // SYNC RNG
              punt.GetAttackPoint(this.m_wptTarget);
              this.m_gidTarget = punt.GetId();
              this.m_wptTargetCenter = { ...this.m_wptTarget };
              this.m_tcTargetRadius = 0;
              this.m_wcMoveDistPerUpdate = this.m_pmuntc.GetMoveDistPerUpdate();
              this.SetState(kstAttack);
              return knHandled;
            }
          }

          this.AdvanceAnimation(this.m_ani); // idle animation

          this.m_cCountdown -= this.m_unvl.GetUpdateCount();
          if (this.m_cCountdown < 0) {
            this.Idle();
            this.m_cCountdown = this.GetIdleCountdown();
          }
          this.m_unvl.MinSkip(this.m_cCountdown);

          this.DefUpdate(); // flashing
          return knHandled;
        }
      }
      return knNotHandled;
    }

    //-----------------------------------------------------------------------
    if (st === kstDying) {
      switch (pmsg.mid) {
        case kmidReservedEnter: {
          // MobileUnit.cpp:1371-1378 — Deactivate, then redraw this part of the
          // minimap (it will skip inactive munts)
          const trc = { left: 0, top: 0, right: 0, bottom: 0 };
          this.GetTileRect(trc);

          this.Deactivate();

          this.ctx.host.MiniMapRedrawTRect(trc.left, trc.top, trc.right, trc.bottom);

          this.m_ff = (this.m_ff ^ (kfGobLayerDepthSorted | kfGobLayerSurfaceDecal)) >>> 0;

          // SfxFromCategory picks with the ASYNC RNG — host-side, never sim-affecting
          this.ctx.host.PlaySfx(`sfxc:${this.m_pmuntc.sfxcDestroyed}`);

          // Remove the corpse in .2 seconds
          this.ctx.smm!.SendDelayedMsgIds(kmidDelete, 20, this.m_gid, this.m_gid);

          const pgobExpl = CreateAnimGob(
            this.ctx,
            this.m_wx,
            this.m_wy,
            kfAnmDeleteWhenDone | kfAnmSmokeFireLayer,
            this.ctx.animDataFor('vexplosion.anir'),
          );
          if (pgobExpl !== null) pgobExpl.SetOwner(this.m_pplr);
          return knHandled;
        }

        case kmidReservedUpdate:
          this.DefUpdate(); // flashing
          return knHandled;

        case kmidDelete:
          this.Delete();
          return knDeleted;

        default:
          return knHandled; // DiscardMsgs: eat everything else (kmidHit, kmidNearbyAllyHit, ...)
      }
    }

    //-----------------------------------------------------------------------
    if (st === kstChangeStatePendingFireComplete) {
      if (pmsg.mid === kmidReservedUpdate) {
        if (!(this.m_wfMunt & kfMuntFiring)) {
          this.SetState(this.m_stPending);
          this.m_stPending = kstReservedNull;
        }
        this.DefUpdate(); // advances any ongoing firing animation
        return knHandled;
      }
      return knNotHandled;
    }

    if (st === kstContinueActionPendingFireComplete) {
      if (pmsg.mid === kmidReservedUpdate) {
        if (!(this.m_wfMunt & kfMuntFiring)) {
          this.ctx.smm!.SendMsg(this.m_msgAction);
          return knHandled;
        }
        this.DefUpdate();
        return knHandled;
      }
      return knNotHandled;
    }

    return knNotHandled;
  }

  // ===== movement machinery =====

  // game/MobileUnit.cpp:1440-1447
  private ReserveTile(tx: number, ty: number, fReserve: boolean): void {
    if (fReserve) this.trmap.SetFlags(tx, ty, 1, 1, kbfMobileUnit);
    else this.trmap.ClearFlags(tx, ty, 1, 1, kbfMobileUnit);
  }

  // game/MobileUnit.cpp:1449-1452
  private IsTileReserved(tx: number, ty: number): boolean {
    return this.trmap.TestFlags(tx, ty, 1, 1, kbfMobileUnit);
  }

  // game/MobileUnit.cpp:1454-1461
  private GetTileFlags(tx: number, ty: number): number {
    const bf = this.trmap.GetFlags(tx, ty);
    return bf === null ? kbfStructure : bf;
  }

  // game/MobileUnit.cpp:1463-1469
  InTransition(): boolean {
    if (this.m_ppathUnit === null) return false;
    return this.m_cMoveStepsRemaining !== 0;
  }

  // game/MobileUnit.cpp:1471-1476
  IsReadyForCommand(): boolean {
    if (this.m_wfMunt & kfMuntFiring) return false;
    return !this.InTransition();
  }

  // game/MobileUnit.cpp:1478-1521
  private NotifyMoveWaitingNearby(wx: number, wy: number): void {
    this.m_wfMunt &= ~kfMuntMoveWaitingNearby;

    // Look around our dst for units trying to get into our dst tile
    const txDst = TcFromWc(wx);
    const tyDst = TcFromWc(wy);

    const { ctx: ctxMap, cty } = this.ctx.gobm.GetMapSize();

    for (let n = 0; n < g_mpDirToDx.length; n++) {
      const tx = txDst + g_mpDirToDx[n];
      const ty = tyDst + g_mpDirToDy[n];
      if (tx < 0 || tx >= ctxMap) continue;
      if (ty < 0 || ty >= cty) continue;

      for (
        let gid = this.ctx.gobm.GetFirstGid(tx, ty);
        gid !== kgidNull;
        gid = this.ctx.gobm.GetNextGid(gid)
      ) {
        const pgob = this.ctx.gobm.GetGob(gid) as MobileUnitGob | null;
        if (pgob === null) continue;
        if (!(pgob.m_ff & kfGobMobileUnit)) continue;

        if (pgob.m_txDst === txDst && pgob.m_tyDst === tyDst) {
          if (pgob.IsMoveWaiting()) {
            this.ctx.smm!.SendMsgIds(kmidMoveWaitingNearby, pgob.GetId());
          }
        }
      }
    }
  }

  // game/MobileUnit.cpp:1523-1551
  private GetReservingUnit(tx: number, ty: number): MobileUnitGob | null {
    // There can be more than one mobile unit in a tile in transition situations
    for (
      let gid = this.ctx.gobm.GetFirstGid(tx, ty);
      gid !== kgidNull;
      gid = this.ctx.gobm.GetNextGid(gid)
    ) {
      const pmunt = this.ctx.gobm.GetGob(gid) as MobileUnitGob | null;
      if (pmunt === null) continue;
      if (!(pmunt.m_ff & kfGobMobileUnit)) continue;

      // The reserving gob either isn't in transition or is in transition to this tile
      if (!pmunt.InTransition() || (pmunt.m_txDst === tx && pmunt.m_tyDst === ty)) {
        return pmunt;
      }
    }

    return AnyTransitionsIntoTile(this.ctx, tx, ty, null);
  }

  // game/MobileUnit.cpp:1594-1633
  protected MoveEnter(fReset: boolean = false): void {
    this.StartAnimation(this.m_ani, this.m_pmuntc.anMovingStripIndices[this.m_dir], 0, kfAniLoop);

    // These reset when this move isn't aware of group behavior
    if (fReset) {
      this.m_wptTargetCenter = { ...this.m_wptTarget };
      this.m_tcTargetRadius = 0;
    }
    if (this.m_wcMoveDistPerUpdate === 0) {
      this.m_wcMoveDistPerUpdate = this.m_pmuntc.GetMoveDistPerUpdate();
    }

    // If the target is in a blocked area, walk back toward this unit (straight line)
    // to the first free tile.
    const txTo = TcFromWc(this.m_wptTarget.wx);
    const tyTo = TcFromWc(this.m_wptTarget.wy);
    if (this.trmap.IsBlocked(txTo, tyTo, 0)) {
      const free = this.trmap.FindFirstUnoccupied(
        txTo,
        tyTo,
        TcFromWc(this.m_wx),
        TcFromWc(this.m_wy),
      );
      if (free !== null) {
        this.m_wptTarget.wx = WcFromTc(free.tx) + kwcTileHalf;
        this.m_wptTarget.wy = WcFromTc(free.ty) + kwcTileHalf;
      }
    }

    // A path is pending: proper waiting semantics depend on knowing this
    this.m_wfMunt |= kfMuntPathPending;
    this.m_wfMunt &= ~kfMuntStuck;
  }

  // game/MobileUnit.cpp:1635-1647
  protected MoveExit(): void {
    this.m_wfMunt &= ~(kfMuntMoveWait | kfMuntPathPending);
    this.m_ppathUnit = null;
    this.m_ppathAvoid = null;

    if (this.m_wfMunt & kfMuntMoveWaitingNearby) {
      this.NotifyMoveWaitingNearby(this.m_wx, this.m_wy);
    }
  }

  // game/MobileUnit.cpp:1649-1765 — the 68K path cache is compiled out on this target;
  // the live path is: close targets try a cheap line path first, then best-first search.
  private FindPath(txFrom: number, tyFrom: number, txTo: number, tyTo: number): Path | null {
    const ktcFastPathRangeMax = 20;
    let ppathT: Path | null = null;
    if (
      ganSquared[Math.abs(txFrom - txTo)] + ganSquared[Math.abs(tyFrom - tyTo)] <
      ganSquared[ktcFastPathRangeMax] * 2
    ) {
      ppathT = this.trmap.FindLinePath(txFrom, tyFrom, txTo, tyTo, kbfStructure);
    }
    if (ppathT === null) {
      ppathT = this.trmap.FindPath(txFrom, tyFrom, txTo, tyTo, kbfStructure);
    }
    return ppathT;
  }

  // game/MobileUnit.cpp:1774-1800
  private PrepPath(wxDst: number, wyDst: number): boolean {
    const txFrom = TcFromWc(this.m_wx);
    const tyFrom = TcFromWc(this.m_wy);
    const txTo = TcFromWc(wxDst);
    const tyTo = TcFromWc(wyDst);
    const ppathT = this.FindPath(txFrom, tyFrom, txTo, tyTo);

    this.m_ppathAvoid = null;
    this.m_ppathUnit = ppathT;
    this.m_itptPath = 0;
    this.m_wfMunt &= ~kfMuntPathPending;
    this.m_cMoveStepsRemaining = 0;

    return this.m_ppathUnit !== null;
  }

  // game/MobileUnit.cpp:1802-1861
  protected MoveUpdate(): number {
    // If a command is pending and now is a good time, handle it
    if (this.m_wfMunt & kfMuntCommandPending && this.IsReadyForCommand()) {
      this.m_wfMunt &= ~kfMuntCommandPending;
      this.ctx.smm!.SendMsg(this.m_msgPending);
      return knMoveMoving;
    }

    // Initialize a path if we haven't yet
    if (this.m_ppathUnit === null) {
      if (this.CheckDestinationReached()) return knMoveTargetReached;

      if (!this.PrepPath(this.m_wptTarget.wx, this.m_wptTarget.wy)) {
        this.m_wfMunt |= kfMuntStuck;
        return knMoveStuck;
      }
    }

    // We have a path. If not in transition, prepare the next transition
    if (!this.InTransition()) {
      const r = this.PrepareNextTransition();
      if (!r.ok) return r.nMoveResult;
      this.EnterTransition(r.tptNext!);
    }

    this.ContinueTransition();
    return knMoveMoving;
  }

  // game/MobileUnit.cpp:1863-1922
  private EnterTransition(ptptNext: TPoint): void {
    // This gob isn't a viable replication candidate in transition
    this.m_wfMunt &= ~kfMuntAtReplicatorInput;

    this.m_txDst = ptptNext.tx;
    this.m_tyDst = ptptNext.ty;

    // Unreserve the current tile, reserve the new one
    const txOld = TcFromWc(this.m_wx);
    const tyOld = TcFromWc(this.m_wy);
    this.ReserveTile(txOld, tyOld, false);
    this.m_dirNext = Direction16FromLocations(txOld, tyOld, ptptNext.tx, ptptNext.ty);

    this.ReserveTile(ptptNext.tx, ptptNext.ty, true);
    this.m_wfMunt |= kfMuntDestinationReserved;

    // Notify gobs waiting on this gob's tile to clear (it just did)
    if (this.m_wfMunt & kfMuntMoveWaitingNearby) {
      this.NotifyMoveWaitingNearby(this.m_wx, this.m_wy);
    }

    this.StartAnimation(
      this.m_ani,
      this.m_pmuntc.anMovingStripIndices[this.m_dir],
      0,
      kfAniLoop | kfAniIgnoreFirstAdvance,
    );

    // Move steps to the next tile, adjusted for diagonals. Now "in transition".
    let wcMoveDist = this.m_wcMoveDistPerUpdate;
    if (Dir16IsDiagonal(this.m_dirNext)) wcMoveDist = gawcDiagonalDist[wcMoveDist];
    this.m_cMoveStepsRemaining = gaDiv256byNWithRounding[wcMoveDist];
  }

  // game/MobileUnit.cpp:1924-1991
  private ContinueTransition(): void {
    // Transition movement doesn't skip updates
    this.m_unvl.MinSkip();

    // Make sure we're facing the way we want to go before we go
    if (this.m_dirNext !== this.m_dir) {
      this.m_dir = TurnToward16(this.m_dirNext, this.m_dir);
      this.StartAnimation(
        this.m_ani,
        this.m_pmuntc.anMovingStripIndices[this.m_dir],
        0,
        kfAniLoop | kfAniIgnoreFirstAdvance,
      );
      return;
    }

    let wxNew = this.m_wx;
    let wyNew = this.m_wy;

    this.m_cMoveStepsRemaining--;
    if (this.m_cMoveStepsRemaining === 0) {
      // Hop to the exact center of the destination tile
      wxNew = WcTrunc(this.m_wx) + kwcTileHalf;
      wyNew = WcTrunc(this.m_wy) + kwcTileHalf;

      // No longer in transition; this bit tells us which tile is reserved
      this.m_wfMunt &= ~kfMuntDestinationReserved;
    } else {
      let wcMoveDist = this.m_wcMoveDistPerUpdate;
      if (Dir16IsDiagonal(this.m_dir)) wcMoveDist = gawcDiagonalDist[wcMoveDist];

      const dx = Dir16ToDx(this.m_dir);
      if (dx === -1) wxNew -= wcMoveDist;
      else if (dx === 1) wxNew += wcMoveDist;

      const dy = Dir16ToDy(this.m_dir);
      if (dy === -1) wyNew -= wcMoveDist;
      else if (dy === 1) wyNew += wcMoveDist;
    }

    this.SetPosition(s16(wxNew), s16(wyNew));

    this.AdvanceAnimation(this.m_ani);

    this.DefUpdate(); // flashing, etc
  }

  // game/MobileUnit.cpp:1993-2044 — move-wait loop (deadlock) detection
  private IsMoveWaitCycle(pmunt: MobileUnitGob): boolean {
    // If nobody is waiting on this unit there can be no cycle
    if (!(this.m_wfMunt & kfMuntMoveWaitingNearby)) return false;

    // An attacking unit isn't mobile, so no cycle
    if (pmunt.m_st === kstAttack) return false;

    // Start at pmunt and see if we loop back to this unit
    let cCycle = 0;
    let pmuntT: MobileUnitGob | null = pmunt;
    while (pmuntT !== null) {
      if (pmuntT === this) return true;

      if (!(pmuntT.m_wfMunt & kfMuntMoveWait)) return false;

      // Walk to who pmuntT is waiting on; a missing reserver is a cycle break
      pmuntT = this.GetReservingUnit(pmuntT.m_txDst, pmuntT.m_tyDst);
      if (pmuntT === null) return false;

      if (cCycle++ > 15) return false;
    }

    return false;
  }

  // game/MobileUnit.cpp:2046-2060
  private MoveWaitForUnit(pmunt: MobileUnitGob, tx: number, ty: number): boolean {
    if (this.IsMoveWaitCycle(pmunt)) return false;

    this.m_wfMunt |= kfMuntMoveWait;
    this.m_txDst = tx;
    this.m_tyDst = ty;
    pmunt.m_wfMunt |= kfMuntMoveWaitingNearby;
    return true;
  }

  // game/MobileUnit.cpp:2062-2097
  private CheckDestinationReached(): boolean {
    const tx = TcFromWc(this.m_wx);
    const ty = TcFromWc(this.m_wy);
    if (tx === TcFromWc(this.m_wptTarget.wx) && ty === TcFromWc(this.m_wptTarget.wy)) return true;

    // Not at dest if we have no path
    if (this.m_ppathUnit === null) return false;

    // See if we're "close enough"
    const txTargetCenter = TcFromWc(this.m_wptTargetCenter.wx);
    const tyTargetCenter = TcFromWc(this.m_wptTargetCenter.wy);
    const dtx = Math.abs(tx - txTargetCenter);
    if (dtx >= gmpDistFromDxy.length) return false;
    const dty = Math.abs(ty - tyTargetCenter);
    if (dty >= gmpDistFromDxy.length) return false;
    if (gmpDistFromDxy[dtx][dty] > this.m_tcTargetRadius) return false;

    // Close enough — but terrain between us and the center means not arrived
    if (this.trmap.IsLineOccupied(tx, ty, txTargetCenter, tyTargetCenter, 0)) return false;

    return true;
  }

  // game/MobileUnit.cpp:2099-2210
  private PrepareNextTransition(): { ok: boolean; tptNext: TPoint | null; nMoveResult: number } {
    // Clear move wait: we're making a fresh decision
    this.m_wfMunt &= ~kfMuntMoveWait;

    // Get the viable transition locations
    const atpt: TPoint[] = [];
    const ctpt = this.GetNextLocations(atpt);
    if (ctpt === 0) {
      // At a replicator input point? Move-wait until the replicator takes over.
      if (this.CheckReplicatorPoint()) {
        this.m_wfMunt |= kfMuntAtReplicatorInput | kfMuntMoveWait;
        return { ok: false, tptNext: null, nMoveResult: knMoveTargetReached };
      }

      if (this.CheckDestinationReached()) {
        return { ok: false, tptNext: null, nMoveResult: knMoveTargetReached };
      }

      this.m_wfMunt |= kfMuntStuck;
      return { ok: false, tptNext: null, nMoveResult: knMoveStuck };
    }

    // First choice clear? Use it (common case).
    const bf = this.GetTileFlags(atpt[0].tx, atpt[0].ty);
    if (!(bf & (kbfStructure | kbfMobileUnit))) {
      this.m_unvl.MinSkip();
      return { ok: true, tptNext: atpt[0], nMoveResult: knMoveMoving };
    }

    // Find reserving munts once
    const apmunt: (MobileUnitGob | null)[] = [];
    const abf: number[] = [];
    for (let itpt = 0; itpt < ctpt; itpt++) {
      abf[itpt] = this.GetTileFlags(atpt[itpt].tx, atpt[itpt].ty);
      if (abf[itpt] & kbfMobileUnit) {
        apmunt[itpt] = this.GetReservingUnit(atpt[itpt].tx, atpt[itpt].ty);
      } else {
        apmunt[itpt] = null;
      }
    }

    // Check open tiles or move wait opportunities
    const cmw = this.ContinueMoveWaiting(apmunt, abf, atpt, ctpt);
    if (cmw.found) {
      if (this.IsMoveWaiting()) {
        return { ok: false, tptNext: null, nMoveResult: knMoveWaiting };
      }
      return { ok: true, tptNext: cmw.tptNext, nMoveResult: knMoveMoving };
    }

    // All move locations occupied. Close enough already?
    if (this.CheckDestinationReached()) {
      return { ok: false, tptNext: null, nMoveResult: knMoveTargetReached };
    }

    // Deal with blockers
    if (this.HandleCollision(apmunt, abf, atpt, ctpt)) {
      if (this.IsMoveWaiting()) {
        return { ok: false, tptNext: null, nMoveResult: knMoveWaiting };
      }

      // Wait an update so the new path options get re-evaluated
      this.m_unvl.MinSkip();
      return { ok: false, tptNext: null, nMoveResult: knMoveMoving };
    }

    this.m_wfMunt |= kfMuntStuck;
    return { ok: false, tptNext: null, nMoveResult: knMoveStuck };
  }

  // game/MobileUnit.cpp:2212-2246
  private HandleCollision(
    apmunt: (MobileUnitGob | null)[],
    abf: number[],
    atpt: TPoint[],
    ctpt: number,
  ): boolean {
    // Ask a standing blocker to move out of the way
    for (let itpt = 0; itpt < ctpt; itpt++) {
      const pmunt = apmunt[itpt];
      if (pmunt === null) continue;
      if (!pmunt.IsMobile()) {
        if (this.PlanMoveAsidePath(pmunt, atpt[itpt].tx, atpt[itpt].ty)) return true;
      }
    }

    // Try to locally path around (the first path works around the other choices)
    if (this.FindLocalAvoidPath(atpt[0].tx, atpt[0].ty)) return true;

    // Find a friendly attacking unit to wait on
    for (let itpt = 0; itpt < ctpt; itpt++) {
      const pmunt = apmunt[itpt];
      if (pmunt === null) continue;
      if (pmunt.m_st === kstAttack && pmunt.IsAlly(this.GetSide())) {
        if (this.MoveWaitForUnit(pmunt, atpt[itpt].tx, atpt[itpt].ty)) return true;
      }
    }

    return false;
  }

  // game/MobileUnit.cpp:2248-2297 (the first loop is #if 0'd out in the C++)
  private ContinueMoveWaiting(
    apmunt: (MobileUnitGob | null)[],
    abf: number[],
    atpt: TPoint[],
    ctpt: number,
  ): { found: boolean; tptNext: TPoint | null } {
    // Try move waiting on the first possible unit
    for (let itpt = 0; itpt < ctpt; itpt++) {
      // If the dest isn't reserved, take it
      const bf = abf[itpt];
      if (!(bf & (kbfStructure | kbfMobileUnit))) {
        return { found: true, tptNext: atpt[itpt] };
      }

      // Deprioritize move-waiting on a munt still in transition
      const pmunt = apmunt[itpt];
      if (pmunt !== null && pmunt.IsMobile()) {
        if (this.MoveWaitForUnit(pmunt, atpt[itpt].tx, atpt[itpt].ty)) {
          return { found: true, tptNext: null };
        }
      }
    }

    return { found: false, tptNext: null };
  }

  // game/MobileUnit.cpp:2299-2382
  private FindLocalAvoidPath(tx: number, ty: number): boolean {
    const kcStepsAvoid = 15;

    const txFrom = TcFromWc(this.m_wx);
    const tyFrom = TcFromWc(this.m_wy);
    const trkpStart = new TrackPoint();
    if (!trkpStart.Init(this.m_ppathUnit!, txFrom, tyFrom, this.m_itptPath, 3)) return false;
    const dirBlocked = DirectionFromLocations(txFrom, tyFrom, tx, ty);

    // Clockwise first
    const atptCW: TPoint[] = [];
    const trkpCW = new TrackPoint();
    const ctptCW = this.FindCloserTrackPoint(
      trkpStart,
      dirBlocked,
      true,
      kcStepsAvoid,
      atptCW,
      trkpCW,
    );

    // Counter-clockwise
    const atptCCW: TPoint[] = [];
    const trkpCCW = new TrackPoint();
    const ctptCCW = this.FindCloserTrackPoint(
      trkpStart,
      dirBlocked,
      false,
      kcStepsAvoid,
      atptCCW,
      trkpCCW,
    );

    // Which one to use?
    let ctptUse: number;
    let atptUse: TPoint[];
    if (ctptCCW > 0) {
      if (ctptCW > 0) {
        if (ctptCW < ctptCCW) {
          ctptUse = ctptCW;
          atptUse = atptCW;
        } else {
          ctptUse = ctptCCW;
          atptUse = atptCCW;
        }
      } else {
        ctptUse = ctptCCW;
        atptUse = atptCCW;
      }
    } else {
      if (ctptCW > 0) {
        ctptUse = ctptCW;
        atptUse = atptCW;
      } else {
        return false;
      }
    }

    const ppath = CreatePathFromPoints(this.trmap, txFrom, tyFrom, atptUse.slice(0, ctptUse));
    if (ppath !== null) {
      // Success: this "avoid" path gets us further along the unit path
      this.m_ppathAvoid = ppath;
      return true;
    }

    return false;
  }

  // game/MobileUnit.cpp:2391-2489
  private FindCloserTrackPoint(
    ptrkpStart: TrackPoint,
    dirBlocked: number,
    fClockwise: boolean,
    ctpt: number,
    atpt: TPoint[],
    ptrkpNew: TrackPoint,
  ): number {
    let pmpDirLastDirScanStart: number[];
    let pmpDirLastDirScanEnd: number[];
    let dirStart: number;
    let dirEnd: number;
    let ddir: number;
    if (fClockwise) {
      pmpDirLastDirScanStart = gmpDirLastDirScanStartCW;
      pmpDirLastDirScanEnd = gmpDirLastDirScanEndCW;
      dirStart = (dirBlocked - 1) & 7;
      dirEnd = (dirStart - 7) & 7;
      ddir = -1;
    } else {
      pmpDirLastDirScanStart = gmpDirLastDirScanStartCCW;
      pmpDirLastDirScanEnd = gmpDirLastDirScanEndCCW;
      dirStart = (dirBlocked + 1) & 7;
      dirEnd = (dirStart + 7) & 7;
      ddir = 1;
    }

    const { ctx: ctxMap, cty } = this.ctx.gobm.GetMapSize();
    const itptClosestSearch = this.m_itptPath > 0 ? this.m_itptPath - 1 : 0;
    ptrkpNew.InitFrom(ptrkpStart);

    const ctptLastGood = 0;
    const txFrom = TcFromWc(this.m_wx);
    const tyFrom = TcFromWc(this.m_wy);
    let txLast = txFrom;
    let tyLast = tyFrom;
    for (let itpt = 0; itpt < ctpt; itpt++) {
      let dirBest = kdirInvalid;
      for (let dirNew = dirStart; dirNew !== dirEnd; dirNew = (dirNew + ddir) & 7) {
        const txNew = txLast + g_mpDirToDx[dirNew];
        const tyNew = tyLast + g_mpDirToDy[dirNew];
        if (txNew < 0 || txNew >= ctxMap) continue;
        if (tyNew < 0 || tyNew >= cty) continue;

        // Can't rely on moving/standing units getting out of the way
        if (this.trmap.IsBlocked(txNew, tyNew, kbfStructure | kbfMobileUnit)) continue;

        dirBest = dirNew;
        break;
      }

      if (dirBest === kdirInvalid) return ctptLastGood;

      atpt[itpt] = { tx: txLast + g_mpDirToDx[dirBest], ty: tyLast + g_mpDirToDy[dirBest] };
      const trkpT = new TrackPoint();
      if (trkpT.Init(this.m_ppathUnit!, atpt[itpt].tx, atpt[itpt].ty, itptClosestSearch, ctpt)) {
        if (ptrkpNew.IsProgress(trkpT)) {
          // If the unit path is accessible from here, return this path
          const tptClosest = trkpT.GetClosestPoint();
          if (
            !this.trmap.IsLineOccupied(
              atpt[itpt].tx,
              atpt[itpt].ty,
              tptClosest.tx,
              tptClosest.ty,
              kbfStructure,
            )
          ) {
            ptrkpNew.InitFrom(trkpT);
            return itpt + 1;
          }
        }
      }

      txLast = atpt[itpt].tx;
      tyLast = atpt[itpt].ty;
      dirStart = pmpDirLastDirScanStart[dirBest];
      dirEnd = pmpDirLastDirScanEnd[dirBest];
    }

    return ctptLastGood;
  }

  // game/MobileUnit.cpp:2491-2620 — ask a chain of allies to move aside
  private PlanMoveAsidePath(pmuntBlocking: MobileUnitGob, txTo: number, tyTo: number): boolean {
    // Enemies won't move aside
    if (!this.IsAlly(pmuntBlocking.GetSide())) return false;

    // Attacking units won't move aside
    if (pmuntBlocking.m_st === kstAttack) return false;

    // The direction this unit is trying to move: priority is orthogonal, back, forward
    const txFrom = TcFromWc(this.m_wx);
    const tyFrom = TcFromWc(this.m_wy);
    const dirMover = DirectionFromLocations(txFrom, tyFrom, txTo, tyTo);

    // Bump the sequence so already-enumerated gobs aren't revisited
    this.ctx.nSeqMoveAside = (this.ctx.nSeqMoveAside + 1) & 0xffff;

    // Mark this unit so it isn't asked to move aside
    this.m_nSeqMoveAside = this.ctx.nSeqMoveAside;

    // Add the first one to the list (s_movd[50])
    const s_movd: MoveDirections[] = [];
    pmuntBlocking.m_nSeqMoveAside = this.ctx.nSeqMoveAside;
    const movd0: MoveDirections = { pmunt: pmuntBlocking, idir: 0, cdir: 0, adir: [] };
    if (!pmuntBlocking.GetMoveAsideDirections(dirMover, movd0)) return false;
    s_movd.push(movd0);
    let imovd = 0;

    // Enumerate mobile units in a chain until an open space is found
    const { ctx: ctxMap, cty } = this.ctx.gobm.GetMapSize();
    let fFound = false;
    while (true) {
      const pmovd = s_movd[imovd];
      const txUnit = TcFromWc(pmovd.pmunt.m_wx);
      const tyUnit = TcFromWc(pmovd.pmunt.m_wy);
      const imovdT = imovd;
      for (; pmovd.idir < pmovd.cdir; pmovd.idir++) {
        const dir = pmovd.adir[pmovd.idir];
        const txNext = txUnit + g_mpDirToDx[dir];
        const tyNext = tyUnit + g_mpDirToDy[dir];
        if (txNext < 0 || txNext >= ctxMap || tyNext < 0 || tyNext >= cty) continue;
        const pmuntNext = this.GetMobileUnitAt(txNext, tyNext);

        // Open space: done, we have a creatable path
        if (pmuntNext === null) {
          fFound = true;
          break;
        }

        // Room to add another? (s_movd[50] cap)
        if (imovd + 1 >= 50) continue;

        // Enemies won't move aside
        if (!this.IsAlly(pmuntNext.GetSide())) continue;

        // Attacking units won't move aside
        if (pmuntNext.m_st === kstAttack) continue;

        // It gets added if it can move in this direction
        const movdNext: MoveDirections = { pmunt: pmuntNext, idir: 0, cdir: 0, adir: [] };
        if (!pmuntNext.GetMoveAsideDirections(dirMover, movdNext)) continue;

        s_movd[imovd + 1] = movdNext;
        imovd++;
        break;
      }

      // If another was added to the list, enumerate it
      if (imovd !== imovdT) continue;

      if (fFound) break;

      // No munt from this munt would move aside; back up and continue
      if (imovd === 0) break;
      imovd--;
      s_movd[imovd].idir++;
    }
    if (!fFound) return false;

    // Walk backwards asking units to move aside
    for (let i = imovd; i >= 0; i--) {
      if (!s_movd[i].pmunt.AcceptMoveAsideRequest(s_movd[i].adir[s_movd[i].idir])) return false;
    }

    // This unit move-waits on the column that is moving aside
    this.MoveWaitForUnit(pmuntBlocking, txTo, tyTo);
    return true;
  }

  // game/MobileUnit.cpp:2622-2642
  private GetMobileUnitAt(tx: number, ty: number): MobileUnitGob | null {
    for (
      let gid = this.ctx.gobm.GetFirstGid(tx, ty);
      gid !== kgidNull;
      gid = this.ctx.gobm.GetNextGid(gid)
    ) {
      const pmunt = this.ctx.gobm.GetGob(gid) as MobileUnitGob | null;
      if (pmunt === null) continue;
      if (!(pmunt.m_ff & kfGobMobileUnit)) continue;
      if (pmunt.IsMobile()) continue;
      if (pmunt.IsMoveWaiting()) continue;
      return pmunt;
    }

    return null;
  }

  // game/MobileUnit.cpp:2659-2741
  GetMoveAsideDirections(dirMover: number, pmovd: MoveDirections): boolean {
    // Special hack for miners
    if (this.m_st === kstMinerRotateForEntry) return false;

    pmovd.pmunt = this;
    pmovd.cdir = 0;
    pmovd.idir = 0;

    const { ctx: ctxMap, cty } = this.ctx.gobm.GetMapSize();
    const adir = s_mpDirToDirsSorted[dirMover];
    const txFrom = TcFromWc(this.m_wx);
    const tyFrom = TcFromWc(this.m_wy);

    for (let idir = 0; idir < 8; idir++) {
      const dir = adir[idir];
      const tx = txFrom + g_mpDirToDx[dir];
      const ty = tyFrom + g_mpDirToDy[dir];
      if (tx < 0 || tx >= ctxMap || ty < 0 || ty >= cty) continue;

      // Blocked by terrain?
      if (this.trmap.IsBlocked(tx, ty, kbfStructure)) continue;

      const pmunt = this.GetMobileUnitAt(tx, ty);

      // Prioritize empty
      if (pmunt === null) {
        // The tile may still be reserved by a munt in transition to it
        if (this.IsTileReserved(tx, ty)) continue;
        pmovd.adir[0] = dir;
        pmovd.cdir = 1;
        return true;
      }

      // Already been asked? If not, it is fresh meat
      if (pmunt.m_nSeqMoveAside === this.ctx.nSeqMoveAside) continue;
      pmunt.m_nSeqMoveAside = this.ctx.nSeqMoveAside;

      // Only non-mobile munts are candidates
      if (pmunt.IsMobile()) continue;

      pmovd.adir[pmovd.cdir++] = dir;
    }

    return pmovd.cdir !== 0;
  }

  // game/MobileUnit.cpp:2743-2773
  AcceptMoveAsideRequest(dir: number): boolean {
    const txFrom = TcFromWc(this.m_wx);
    const tyFrom = TcFromWc(this.m_wy);
    const txTo = txFrom + g_mpDirToDx[dir];
    const tyTo = tyFrom + g_mpDirToDy[dir];

    // Issue the move command. These occur in order so earlier units can move-wait on
    // later units. Call MobileUnitGob.SetTarget DIRECTLY (not the override) so behavior
    // like the miner's mining doesn't interfere.
    MobileUnitGob.prototype.SetTarget.call(
      this,
      kgidNull,
      WcFromTc(txTo) + kwcTileHalf,
      WcFromTc(tyTo) + kwcTileHalf,
    );
    return true;
  }

  // game/MobileUnit.cpp:2775-2818
  private GetNextLocations(atpt: TPoint[]): number {
    // Use the avoid path if it exists
    let cStepsFurtherStop = 3;
    if (this.m_ppathAvoid !== null) {
      // At the avoid path's destination yet?
      const txFrom = TcFromWc(this.m_wx);
      const tyFrom = TcFromWc(this.m_wy);
      let fEnd = false;
      const tptT = this.m_ppathAvoid.GetPoint(this.m_ppathAvoid.GetCount() - 1, 0);
      if (tptT !== null) {
        if (tptT.tx === txFrom && tptT.ty === tyFrom) fEnd = true;
      } else {
        fEnd = true;
      }

      // More to avoid? Do it.
      if (!fEnd) {
        const itptDummy = { value: 0 };
        const ctpt = this.GetNextLocations2(
          this.m_ppathAvoid,
          itptDummy,
          this.m_ppathAvoid.GetCount(),
          atpt,
        );
        if (ctpt !== 0) return ctpt;
      }

      // The avoid path is done; drop it
      cStepsFurtherStop = Math.min(cStepsFurtherStop, this.m_ppathAvoid.GetCount());
      this.m_ppathAvoid = null;
    }

    // Use the unit path
    const itptRef = { value: this.m_itptPath };
    const ctpt = this.GetNextLocations2(this.m_ppathUnit!, itptRef, cStepsFurtherStop, atpt);
    this.m_itptPath = itptRef.value;
    if (ctpt === 0) return 0;
    if (this.m_itptPath > 1) this.m_ppathUnit!.SetCacheIndex(this.m_itptPath - 2);
    return ctpt;
  }

  // game/MobileUnit.cpp:2820-2945 — progress is measured against the unit path; the
  // surrounding tiles are profiled and better-progress positions returned as candidates.
  private GetNextLocations2(
    ppath: Path,
    pitptStart: { value: number },
    cStepsFurtherStop: number,
    atpt: TPoint[],
  ): number {
    const txFrom = TcFromWc(this.m_wx);
    const tyFrom = TcFromWc(this.m_wy);
    const trkpStart = new TrackPoint();
    if (!trkpStart.Init(ppath, txFrom, tyFrom, pitptStart.value, cStepsFurtherStop)) return 0;
    const tptClosest = trkpStart.GetClosestPoint();
    const itptClosest = trkpStart.GetClosestPointIndex();
    pitptStart.value = itptClosest;

    // At the end? Done.
    if (itptClosest === ppath.GetCount() - 1) {
      if (tptClosest.tx === txFrom && tptClosest.ty === tyFrom) return 0;
    }

    // Close to the next step already?
    const tptNext = ppath.GetPoint(itptClosest + 1, kbfStructure | kbfMobileUnit);
    if (tptNext !== null) {
      // The next step exists and is free of obstacles; if we're on the path, take it
      if (Math.abs(tptNext.tx - txFrom) <= 1 && Math.abs(tptNext.ty - tyFrom) <= 1) {
        atpt[0] = tptNext;
        return 1;
      }
    }

    // Maybe not on "next" but closer to closest — take that
    const bf = this.GetTileFlags(tptClosest.tx, tptClosest.ty);
    if (!(bf & (kbfStructure | kbfMobileUnit))) {
      if (Math.abs(tptClosest.tx - txFrom) <= 1 && Math.abs(tptClosest.ty - tyFrom) <= 1) {
        atpt[0] = tptClosest;
        return 1;
      }
    }

    const tptAfter = trkpStart.GetAfterPoint();

    // Profile the surrounding tiles for progress along the unit path
    const itptClosestMeasure = itptClosest === 0 ? 0 : itptClosest - 1;
    const atrkp: TrackPoint[] = [];
    let ctpt = 0;
    const { ctx: ctxMap, cty } = this.ctx.gobm.GetMapSize();
    for (let dir = 0; dir < 8; dir++) {
      const tx = txFrom + g_mpDirToDx[dir];
      const ty = tyFrom + g_mpDirToDy[dir];
      if (tx < 0 || tx >= ctxMap || ty < 0 || ty >= cty) continue;
      if (this.trmap.IsBlocked(tx, ty, 0)) continue;
      atrkp[ctpt] = new TrackPoint();
      atrkp[ctpt].Init(ppath, tx, ty, itptClosestMeasure, 1);
      if (trkpStart.IsProgress(atrkp[ctpt])) {
        // Blocked by terrain means not valid progress
        if (this.trmap.IsLineOccupied(tx, ty, tptAfter.tx, tptAfter.ty, 0)) continue;

        ctpt++;
      }
    }

    // No unblocked positions: take the next one even if blocked; callers move around it
    if (ctpt === 0) {
      for (let dir = 0; dir < 8; dir++) {
        const tx = txFrom + g_mpDirToDx[dir];
        const ty = tyFrom + g_mpDirToDy[dir];
        if (tx < 0 || tx >= ctxMap || ty < 0 || ty >= cty) continue;
        const trkp0 = new TrackPoint();
        trkp0.Init(ppath, tx, ty, itptClosestMeasure, 1);
        if (trkpStart.IsProgress(trkp0)) {
          atpt[0] = { tx, ty };
          return 1;
        }
      }
    }

    // Sort the candidates (verbatim bubble with IsBetterSort)
    const an: number[] = [];
    for (let i = 0; i < ctpt; i++) an[i] = i;
    for (let i = ctpt - 1; i >= 0; i--) {
      for (let j = 1; j <= i; j++) {
        if (atrkp[an[j - 1]].IsBetterSort(atrkp[an[j]])) {
          const n = an[j - 1];
          an[j - 1] = an[j];
          an[j] = n;
        }
      }
    }
    for (let i = 0; i < ctpt; i++) atpt[i] = atrkp[an[i]].GetInitialPoint();

    return ctpt;
  }

  // game/MobileUnit.cpp:2947-2952
  IsMobile(): boolean {
    if (
      this.m_ppathUnit === null &&
      !(this.m_wfMunt & (kfMuntPathPending | kfMuntAtReplicatorInput))
    ) {
      return false;
    }
    return true;
  }

  // game/MobileUnit.cpp:2954-2966 — scans ReplicatorGob's input-point registry
  private CheckReplicatorPoint(): boolean {
    const tx = TcFromWc(this.m_wx);
    const ty = TcFromWc(this.m_wy);
    for (let iRep = 0; iRep < this.ctx.cReplicatorInputPoints; iRep++) {
      const tptReplicator = this.ctx.replicatorInputPoints[iRep];
      if (tptReplicator.tx === tx && tptReplicator.ty === ty) return true;
    }
    return false;
  }

  // game/MobileUnit.cpp:2968-3035
  override SetPosition(wx: number, wy: number): void {
    if (this.m_wx === wx && this.m_wy === wy) return;

    // Notify waiters
    if (!this.InTransition()) {
      if (this.m_wfMunt & kfMuntMoveWaitingNearby) {
        this.NotifyMoveWaitingNearby(this.m_wx, this.m_wy);
      }
    }

    // The C++ gates MarkRedraw on PcFromWc pixel change — a zoom-dependent presentation
    // table. The redraw flag is masked out of TrackState, so unconditional is equivalent.
    this.MarkRedraw();

    // Don't sleep on this important new development
    this.m_unvl.MinSkip();

    // Keep GobMgr in the loop for depth sorting
    const fTileChange = this.ctx.gobm.MoveGob(this, this.m_wx, this.m_wy, wx, wy);
    const wxOld = this.m_wx;
    const wyOld = this.m_wy;
    this.m_wx = s16(wx);
    this.m_wy = s16(wy);

    if (fTileChange) {
      // Fog reveal for the LOCAL player (per-client, unhashed). MobileUnit.cpp:3002-3011
      // uses GetTilePosition (raw m_wx/m_wy), NOT GetCenter — they differ by the
      // UI-bounds offset, which can land one tile over.
      if (this.m_pplr === this.ctx.plrLocal) {
        this.ctx.host.RevealFog(
          this,
          (this.m_puntc.wf & kfUntcLargeDefog) !== 0,
          TcFromWc(this.m_wx),
          TcFromWc(this.m_wy),
        );
      }

      // MobileUnit.cpp:3016-3024 — update minimap (`if (gpmm != NULL)`): the
      // union of the OLD and NEW tile points (raw m_wx/m_wy, not GetTileRect)
      {
        const txOld = TcFromWc(wxOld);
        const tyOld = TcFromWc(wyOld);
        const txNew = TcFromWc(this.m_wx);
        const tyNew = TcFromWc(this.m_wy);
        this.ctx.host.MiniMapRedrawTRect(
          Math.min(txOld, txNew),
          Math.min(tyOld, tyNew),
          Math.max(txOld, txNew) + 1,
          Math.max(tyOld, tyNew) + 1,
        );
      }

      // Notify enemies of this gob nearby
      this.NotifyEnemyNearby();

      // Move between areas
      const amOld = this.ctx.gobm.CalcAreaMask(TcFromWc(wxOld), TcFromWc(wyOld));
      const amNew = this.ctx.gobm.CalcAreaMask(TcFromWc(this.m_wx), TcFromWc(this.m_wy));
      this.ctx.gobm.MoveGobBetweenAreas(this.m_gid, amOld, amNew);
    }
  }

  // game/MobileUnit.cpp:3037-3049 — NOTE the C++ ignores the nArea parameter and reads
  // m_msgAction.GuardAreaCommand.nArea inside the loop; preserved verbatim.
  private FindValidTargetInArea(_nArea: number): UnitGob | null {
    const enm = new Enum();
    while (true) {
      const puntTarget = this.ctx.gobm.EnumGobsInArea(
        enm,
        this.m_msgAction.GuardAreaCommand.nArea,
        ~this.m_pplr.GetAllies(),
        kumMobileUnits,
      );
      if (puntTarget === null) return null;
      if (this.IsValidTarget(puntTarget)) return puntTarget as UnitGob;
    }
  }
}

// MobileUnitGob.InitFromArgs scans the SAME level value string the UnitGob parse consumed,
// continuing at arg 7: "%*d ,%*d ,%*d ,%*d ,%*d ,%*d, %d ,%a" (MobileUnit.cpp:249).
import { IniScanf } from '../data/ini.ts';
function IniScanfBridge(value: string): { count: number; values: (number | string)[] } {
  const { count, values } = IniScanf(value, '%*d ,%*d ,%*d ,%*d ,%*d ,%*d, %d ,%a');
  return { count, values: values as (number | string)[] };
}
